import {
  requireVerifiedSourceWrite,
  requireVerifiedPart,
  type PartWriteResponse,
} from "./partPersistence";
/**
 * Axios 客户端 · JWT Bearer 优先, 开发头兜底。
 */
import axios, { AxiosError, type AxiosRequestTransformer } from "axios";
import { emitApiError } from "@/utils/api-error-bus";

export const apiClient = axios.create({
  baseURL: "",
  timeout: 60_000, // 上传可能慢
});

// 标记 auth API · 401 时不需要 refresh 重试 (避免死循环)
const AUTH_PATHS_NO_RETRY = [
  "/v1/auth/login",
  "/v1/auth/register",
  "/v1/auth/refresh",
  "/v1/auth/accept-invite",
];

type Credentials = {
  access: string | null;
  refresh: string | null;
  fallback: string;
};
type LoginOwner = { credentials: Credentials };
type DispatchOwner = { login: LoginOwner; access: string | null };
let currentLogin: LoginOwner | null = null;
// Weak keys live only as long as the actual dispatched request. No token history in config/DOM/storage.
const dispatchOwners = new WeakMap<object, DispatchOwner | null>();
const credentials = (): Credentials => {
  const access = localStorage.getItem("cnc.access_token");
  return {
    access,
    refresh: localStorage.getItem("cnc.refresh_token"),
    fallback:
      !access && import.meta.env.DEV
        ? JSON.stringify([
            localStorage.getItem("cnc.tenant_id"),
            localStorage.getItem("cnc.user_id"),
            localStorage.getItem("cnc.role"),
          ])
        : "",
  };
};
const sameCredentials = (a: Credentials, b: Credentials) =>
  a.access === b.access && a.refresh === b.refresh && a.fallback === b.fallback;
function loginOwner(): LoginOwner {
  const now = credentials();
  if (!currentLogin || !sameCredentials(currentLogin.credentials, now))
    currentLogin = { credentials: now };
  return currentLogin;
}

apiClient.interceptors.request.use((config) => {
  const accessToken = localStorage.getItem("cnc.access_token");
  if (accessToken) {
    config.headers.set("Authorization", `Bearer ${accessToken}`);
  } else {
    // 2026-05-21: 生产模式 (有真 JWT auth) 不再走 dev header fallback.
    // 仅当 import.meta.env.DEV (vite 开发模式) 且 localStorage 显式设了
    // cnc.tenant_id/user_id 时才用 dev header (向后兼容老开发流程).
    if (import.meta.env.DEV) {
      const tenantId = localStorage.getItem("cnc.tenant_id");
      const userId = localStorage.getItem("cnc.user_id");
      if (tenantId && userId) {
        const role = localStorage.getItem("cnc.role") ?? "platform_ops";
        config.headers.set("X-Tenant-Id", tenantId);
        config.headers.set("X-User-Id", userId);
        config.headers.set("X-User-Role", role);
      }
    }
  }
  const previous = config.transformRequest;
  // request interceptors run before transforms; this final transform sees the actual dispatch header.
  const captureOwner: AxiosRequestTransformer = function (data, headers) {
    const login = loginOwner();
    const access = login.credentials.access;
    const sent = headers.get("Authorization");
    const [tenant, user, role] = login.credentials.fallback
      ? JSON.parse(login.credentials.fallback)
      : [];
    const devMatches =
      tenant && user
        ? headers.get("X-Tenant-Id") === tenant &&
          headers.get("X-User-Id") === user &&
          headers.get("X-User-Role") === (role ?? "platform_ops")
        : !headers.get("X-Tenant-Id") &&
          !headers.get("X-User-Id") &&
          !headers.get("X-User-Role");
    dispatchOwners.set(
      this,
      access
        ? sent === `Bearer ${access}`
          ? { login, access }
          : null
        : !sent && devMatches
          ? { login, access }
          : null,
    );
    return data;
  };
  config.transformRequest = [
    ...(Array.isArray(previous) ? previous : previous ? [previous] : []),
    captureOwner,
  ];
  return config;
});

// 标准化错误格式
/**
 * API 错误 · 带状态码 + 业务 code · 让页面层能分支处理 (403 vs 500 等).
 *
 * 用法:
 *   try { ... } catch (e) {
 *     if (e instanceof ApiError && e.status === 403) showPermDenied();
 *     else if (e instanceof ApiError && e.status === 0) showNetworkError();
 *   }
 */
export class ApiError extends Error {
  status: number; // HTTP 状态 · 0 = 网络/上游不可达
  code: string | null; // 业务 code (如 AUDIT_READ_FORBIDDEN) · null 表示无结构 detail
  /** 结构化 detail 原文(M1-4 · 业务错误可携带附加数据 · 如 TIME_UNRESOLVED 的相似件参考带)。 */
  detail?: unknown;
  constructor(
    message: string,
    status: number,
    code: string | null,
    detail?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.detail = detail;
  }
}

// ─── 大件 CAD「后台处理中」(HTTP 202 · code=CAD_PREVIEW_PENDING) ────────
// 后端对超阈值(~30MB)STEP 的 /mesh /assembly 走异步路由:立即返
// 202 {code:"CAD_PREVIEW_PENDING", message:"大件后台处理中"},后台 worker
// 拆解+粗镶嵌缓存,完成后同端点自然 200。202 body 既不是可用数据(mesh 调用方
// 会把 JSON 字节当 STL 解析),也不是错误(不该全局 toast)→ 响应拦截器把它转成
// status=202 的 ApiError reject,调用方 isCadPreviewPending() 识别后显示
// "处理中"并有限轮询(见 api/cadPending.ts fetchWithPendingPoll)。

/** 大件 CAD 后台处理中的业务码 (HTTP 202). */
export const CAD_PREVIEW_PENDING_CODE = "CAD_PREVIEW_PENDING";

/**
 * 后端两种 202 pending 形态(都归一成 code=CAD_PREVIEW_PENDING 的 ApiError):
 * - /mesh: JSONResponse 顶层 {code:"CAD_PREVIEW_PENDING"}
 * - /assembly、逐件端点: HTTPException(202) → {detail:{code:"CAD_DECOMPOSE_PENDING"}}
 *   (FastAPI 把 detail 包一层 · 后台拆解 pending 用独立业务码)
 */
const CAD_PENDING_CODES: ReadonlySet<string> = new Set([
  CAD_PREVIEW_PENDING_CODE,
  "CAD_DECOMPOSE_PENDING",
]);

/** e 是否「大件后台处理中」(202 + CAD_PREVIEW_PENDING) · 调用方显示处理中并轮询. */
export function isCadPreviewPending(e: unknown): e is ApiError {
  return (
    e instanceof ApiError &&
    e.status === 202 &&
    e.code === CAD_PREVIEW_PENDING_CODE
  );
}

/** 202 body → {code,message} · 按 responseType 可能是对象/arraybuffer/blob/text · 非 pending 返 null. */
async function _readCadPendingBody(
  data: unknown,
): Promise<{ code: string; message?: string } | null> {
  try {
    let obj: unknown = data;
    if (data instanceof ArrayBuffer) {
      obj = JSON.parse(new TextDecoder("utf-8").decode(data));
    } else if (data instanceof Blob) {
      obj = JSON.parse(await data.text());
    } else if (typeof data === "string") {
      obj = JSON.parse(data);
    }
    // HTTPException(202) 形态:pending 载荷包在 detail 里 → 剥一层再判 code
    if (obj !== null && typeof obj === "object") {
      const detail = (obj as { detail?: unknown }).detail;
      if (detail !== null && typeof detail === "object") obj = detail;
    }
    if (
      obj !== null &&
      typeof obj === "object" &&
      typeof (obj as { code?: unknown }).code === "string" &&
      CAD_PENDING_CODES.has((obj as { code: string }).code)
    ) {
      return obj as { code: string; message?: string };
    }
  } catch {
    /* 非 JSON body → 非 pending · 走正常 resolve */
  }
  return null;
}

/**
 * 202 + code=CAD_PREVIEW_PENDING → 以 ApiError(status=202) reject · 其余原样放行.
 * 其他 202 端点(如 optimize 异步受理 · body 无此 code)不受影响。
 * 不走 _rejectWith → 不触发全局错误 toast(处理中不是错误)。导出供单测直接驱动。
 */
export async function rejectCadPreviewPending<
  R extends { status: number; data: unknown },
>(resp: R): Promise<R> {
  if (resp.status !== 202) return resp;
  const pending = await _readCadPendingBody(resp.data);
  if (!pending) return resp;
  throw new ApiError(
    `[${CAD_PREVIEW_PENDING_CODE}] ${pending.message ?? "大件后台处理中"}`,
    202,
    CAD_PREVIEW_PENDING_CODE,
  );
}

// 后端错误 detail 可能 3 种形态:
//   1. {code, message} — 业务自定义 (HTTPException(detail={...}))
//   2. "字符串" — FastAPI HTTPException(detail="xxx") 默认
//   3. [{loc, msg, type}, ...] — Pydantic 422 自动校验失败
type PydanticIssue = {
  loc?: (string | number)[];
  msg?: string;
  type?: string;
};
type BackendDetail =
  | { code?: string; message?: string }
  | string
  | PydanticIssue[];

/** axios config 扩展 · per-request 错误 toast 行为. */
declare module "axios" {
  export interface AxiosRequestConfig {
    /** 该请求出错时不自动 toast (页面自己处理). 默认 false (会自动 toast). */
    _silentError?: boolean;
    /** 该请求失败时全局 toast 显示的动作上下文, 如 "签字失败". */
    _errorContext?: string;
  }
}

function _rejectWith(apiErr: ApiError, error: AxiosError) {
  // 触发全局 bus (除非该请求设了 _silentError)
  const cfg = error.config as
    | { _silentError?: boolean; _errorContext?: string }
    | undefined;

  // 2026-05-23 fix · 所有 401 一律 silent · 跳 /login 本身就是用户感知
  // 再来个 toast "未登录已过期" 是噪音 · 且在跳转过程中 path 检查可能 race
  // (page A → 401 → 跳 /login · 但 path 检查时仍在 page A · silent 不命中)
  // 401 = 跳转用户能看到登录页 · 不需要 toast 多余提示
  const isAuthError = apiErr.status === 401;

  if (!cfg?._silentError && !isAuthError) {
    // 事件总线无 Axios 依赖；静态导入没有循环，也不会制造无效拆包。
    emitApiError(apiErr, cfg?._errorContext);
  }
  return Promise.reject(apiErr);
}

// 401 处理: refresh token 自动续期 · 失败跳 /login
// 用 Promise 缓存防多请求并发 refresh
type RefreshPartition = {
  login: LoginOwner;
  access: string | null;
  refresh: string;
  promise: Promise<string | null>;
};
let refreshPartition: RefreshPartition | null = null;

/**
 * V61 (2026-05-25) · export 给 chat.ts 等 raw fetch 路径用 ·
 * 它们走 SSE 不经 axios interceptor · 需要手动 401 → refresh.
 *
 * 返新 access_token (成功) 或 null (refresh token 也过期 · 需重登).
 * 并发 5s 内合并 · 同一波多请求只调一次 /auth/refresh.
 */
export async function tryRefreshToken(): Promise<string | null> {
  return _tryRefresh();
}

async function _tryRefresh(): Promise<string | null> {
  const login = loginOwner();
  const before = login.credentials;
  const { access, refresh } = before;
  if (!refresh) return null;
  if (
    refreshPartition?.login === login &&
    refreshPartition.access === access &&
    refreshPartition.refresh === refresh
  )
    return refreshPartition.promise;
  // 分区只在内存比对凭证；旧登录的请求无权写入或清除后来的登录。
  const partition: RefreshPartition = {
    login,
    access,
    refresh,
    promise: Promise.resolve(null),
  };
  const owns = () =>
    loginOwner() === login && sameCredentials(login.credentials, before);
  refreshPartition = partition;
  partition.promise = (async () => {
    try {
      const { data } = await axios.post<{
        access_token: string;
        refresh_token: string;
      }>("/v1/auth/refresh", { refresh_token: refresh });
      if (!owns()) return null;
      localStorage.setItem("cnc.access_token", data.access_token);
      localStorage.setItem("cnc.refresh_token", data.refresh_token);
      // Only our own successful refresh establishes A→B continuity for in-flight request owners.
      login.credentials = credentials();
      partition.access = data.access_token;
      partition.refresh = data.refresh_token;
      return data.access_token;
    } catch {
      if (owns()) {
        localStorage.removeItem("cnc.access_token");
        localStorage.removeItem("cnc.refresh_token");
        login.credentials = credentials();
      }
      return null;
    } finally {
      // 5s 后清缓存允许下次重试 · 但同一波并发只 refresh 一次
      setTimeout(() => {
        if (refreshPartition === partition) refreshPartition = null;
      }, 5_000);
    }
  })();
  return partition.promise;
}

apiClient.interceptors.response.use(
  (r) => rejectCadPreviewPending(r),
  async (error: AxiosError<{ detail?: BackendDetail }>) => {
    // 取消(AbortController / axios CancelToken)不是 API 错误 · 原样抛出。
    // 2026-09-18 实测:这里曾把 CanceledError 也包成 ApiError(status=0, code=null),
    // 调用方的 `code === "ERR_CANCELED"` 守卫从此一个都拦不住 —— 驾驶舱每次切机床 /
    // 切 Tab / 隐藏页掐掉在途轮询,都被写成「服务暂不可达」横幅 + 全局 toast
    // (prod nginx 5 天 167 条 view/history 499,每条都变过一次假告警)。
    // 取消也不能进 emitApiError:掐掉的请求没有任何用户可感知的失败可言。
    if (axios.isCancel(error)) return Promise.reject(error);
    let detail = error.response?.data?.detail;
    const status = error.response?.status ?? 0;
    const reqUrl = error.config?.url ?? "";
    const isAuthEndpoint = AUTH_PATHS_NO_RETRY.some((p) => reqUrl.endsWith(p));

    // 401: 尝试 refresh · 失败跳 /login (但 auth endpoint 自己 401 不重试)
    if (status === 401 && !isAuthEndpoint && error.config) {
      // 防重入: 加 _retried 标记
      const cfg = error.config as {
        _retried?: boolean;
        _silentError?: boolean;
      };
      if (!cfg._retried) {
        cfg._retried = true;
        const dispatched = dispatchOwners.get(error.config);
        const originalLogin = dispatched && dispatched.login === loginOwner();
        if (originalLogin) {
          // A late A response can use B/C only when this helper has advanced that same owner.
          // This proof outlives the 5s request-coalescing cache, bounded by in-flight request lifetime.
          const alreadyRenewed =
            dispatched.access !== dispatched.login.credentials.access;
          const newToken = alreadyRenewed
            ? dispatched.login.credentials.access
            : await _tryRefresh();
          const refreshedOwner =
            newToken &&
            loginOwner() === dispatched.login &&
            dispatched.login.credentials.access === newToken;
          if (refreshedOwner) {
            error.config.headers["Authorization"] = `Bearer ${newToken}`;
            const previous = error.config.transformRequest;
            // request拦截器是异步链。临近dispatch再核凭证，防重试排队期间被另一登录换成新Bearer。
            const checkRetryOwner: AxiosRequestTransformer = function (
              data,
              headers,
            ) {
              const current = loginOwner();
              if (
                current !== dispatched.login ||
                !current.credentials.access ||
                headers.get("Authorization") !==
                  `Bearer ${current.credentials.access}`
              )
                throw new axios.CanceledError("登录身份已变化", this);
              return data;
            };
            error.config.transformRequest = [
              ...(Array.isArray(previous)
                ? previous
                : previous
                  ? [previous]
                  : []),
              checkRetryOwner,
            ];
            return apiClient.request(error.config);
          }
          // helper对原登录过期自清空、原登录没有refresh仍走既有跳转；另一非空登录的凭证不受旧401驱动。
          if (
            loginOwner() === dispatched.login &&
            window.location.pathname !== "/login"
          ) {
            window.location.href = `/login?from=${encodeURIComponent(window.location.pathname)}`;
          }
        }
        // ★ 标 silent · 避免页面跳转过程中弹 "未登录已过期" toast (噪音)
        // 多个并发受保护请求同时 401 时, 否则会弹 N 个相同 toast
        cfg._silentError = true;
      } else {
        // 已重试过 (refresh 后再次 401 · 通常 token 立即失效) · 也 silent
        (error.config as { _silentError?: boolean })._silentError = true;
      }
    }

    // /login /register 等 auth 页内的任何受保护 API 错误都 silent
    // (用户已经在登录页, 不需要再弹 "操作失败" / "未登录" 等噪音)
    const path = window.location.pathname;
    if (
      /^\/(login|register|forgot-password|reset-password|verify-email)\b/.test(
        path,
      ) &&
      error.config
    ) {
      (error.config as { _silentError?: boolean })._silentError = true;
    }

    // responseType:"blob" 的请求出错时 · axios 把 JSON 错误体当 Blob · detail 取不到 →
    // 读出 blob 文本解析回 {detail} · 让下游拿到真实 code/message(否则落兜底丢码 ·
    // 出图/下载等 blob 端点的 422 会被谎报成「[HTTP 422] Request failed」)。
    if (detail === undefined && error.response?.data instanceof Blob) {
      try {
        const parsed = JSON.parse(await error.response.data.text()) as {
          detail?: BackendDetail;
        };
        detail = parsed.detail;
      } catch {
        /* 非 JSON blob(真二进制)· 维持 undefined 走兜底 */
      }
    }

    // 3. Pydantic 422 数组
    if (Array.isArray(detail)) {
      const summary = detail
        .map((i) => {
          const path = i.loc ? i.loc.slice(1).join(".") : "?";
          return `${path}: ${i.msg ?? "invalid"}`;
        })
        .slice(0, 3)
        .join("; ");
      return _rejectWith(
        new ApiError(
          `[VALIDATION_ERROR] ${summary}${detail.length > 3 ? "..." : ""}`,
          status,
          "VALIDATION_ERROR",
        ),
        error,
      );
    }

    // 1. 结构化 {code, message} · detail 原文随错误对象带出(业务附加数据 · 如参考带)
    if (
      detail &&
      typeof detail === "object" &&
      (detail.code || detail.message)
    ) {
      return _rejectWith(
        new ApiError(
          `[${detail.code ?? "ERROR"}] ${detail.message ?? "(no message)"}`,
          status,
          detail.code ?? null,
          detail,
        ),
        error,
      );
    }

    // 2. 字符串 detail
    if (typeof detail === "string") {
      return _rejectWith(
        new ApiError(`[HTTP ${status}] ${detail}`, status, null),
        error,
      );
    }

    // 兜底: 网络错 / 上游不可达 / 没响应 body
    const fallback = error.message || `HTTP ${status || "network error"}`;
    return _rejectWith(
      new ApiError(`[HTTP ${status || "—"}] ${fallback}`, status, null),
      error,
    );
  },
);

export type RiskCounts = { A: number; B: number; C: number; i: number };
export type Severity = "A" | "B" | "C" | "i";
export type ReviewStatus = "pending" | "approved" | "rejected";
export type ParseQuality = "complete" | "degraded";

export interface UploadResult {
  program_id: string;
  status: "completed" | "duplicate" | "duplicate_revived";
  risks?: RiskCounts;
  parse_quality?: ParseQuality;
}

// 2026-05-21: TokenPair / login / refreshToken 已迁到 @/api/auth.ts (统一管理).
// 此处保留 re-export 以兼容老引用 (此前没人用 · 安全删).

export interface ProgramListItem {
  id: string;
  filename: string;
  uploaded_at: string;
  review_status: ReviewStatus;
  parse_quality: ParseQuality;
  total_lines: number;
  risks: RiskCounts;
  machine_id?: string | null;
  part_id?: string | null;
}

export interface ProgramListResponse {
  total: number;
  limit: number;
  offset: number;
  items: ProgramListItem[];
}

export interface MultiSetupMeta {
  schema_version: number;
  setup_directions: number[][]; // 各工位刀轴(件系单位向量)
  planned_coverage: number; // 规划器合计可达覆盖率 0..1
  final_overcut_mm3: number; // 跨工位合并过切(raw 含外皮口径)
  final_undercut_mm3: number; // 跨工位合并欠切(box 上界估计 · 含 3 轴诚实残留)
  removed_per_setup: number[]; // 各工位去除体积
  setups_object_key: string;
  stock_object_key: string;
  // RFC-008 S4d: 5轴联动收尾(可选 · 旧程序/纯 3+2 无)
  final_overcut_core_mm3?: number; // 公差带过切(联动程序收敛判据口径)
  five_axis_object_key?: string; // 存在 = 程序含联动收尾段
  five_axis_removed_mm3?: number; // 联动段去除体积
}

/** 生成溯源块 · 仅 source=ai_generation 且能反查到 GenerateRun 时非空 (详情页溯源卡). */
export interface ProgramGeneration {
  generate_id: string;
  part_id: string;
  status: GenerateStatus;
  converged: boolean;
  current_round: number;
  max_rounds: number;
  /** 本程序是否该 run 的终轮放行件 (false=中间轮产物). */
  is_final_program: boolean;
  reference_program_id: string | null;
}

export interface ProgramDetail {
  id: string;
  filename: string;
  uploaded_at: string;
  uploaded_by: string;
  encoding: string;
  sha256: string;
  source: string;
  dialect: string;
  parse_quality: ParseQuality;
  parse_duration_ms: number;
  machine_id?: string | null;
  part_id?: string | null;
  review: {
    status: ReviewStatus;
    reviewer_id?: string | null;
    reviewed_at?: string | null;
    remarks?: string | null;
    signature_hash?: string | null;
  };
  stats: {
    total_lines: number;
    risks: RiskCounts;
  };
  ir: Record<string, unknown>;
  multi_setup_meta?: MultiSetupMeta | null; // RFC-005: 3+2 多工位元数据(null=单工位)
  generation?: ProgramGeneration | null; // 生成溯源(null=上传/未链接 · ai_generation 才反查)
}

export interface RiskItem {
  rule_id: string;
  severity: Severity;
  line_numbers: number[];
  title: string;
  description: string;
  suggestion: string;
  confidence: number;
  evidence: string[];
}

export interface RuleEvaluationError {
  rule_id: string;
  code: string;
  message: string;
}

export interface ReportData {
  program_id: string;
  evaluated_at: string;
  evaluation_duration_ms: number;
  rule_set_version: string;
  risks: RiskItem[];
  llm_explanations: Record<string, unknown>;
  evaluation_errors?: RuleEvaluationError[];
}

export interface ReviewResult {
  id: string;
  review_status: ReviewStatus;
  reviewer_id: string;
  reviewed_at: string | null;
  signature_hash: string;
}

export async function uploadProgram(
  file: File,
  opts: { machineId?: string; partId?: string; silent?: boolean } = {},
): Promise<UploadResult> {
  const form = new FormData();
  form.append("file", file);
  if (opts.machineId) form.append("machine_id", opts.machineId);
  if (opts.partId) form.append("part_id", opts.partId);

  const { data } = await apiClient.post<UploadResult>(
    "/v1/nc-review/programs",
    form,
    {
      headers: { "Content-Type": "multipart/form-data" },
      _errorContext: "上传失败",
      ...(opts.silent ? { _silentError: true } : {}),
    },
  );
  return data;
}

export async function listPrograms(
  params: {
    status?: ReviewStatus;
    machineId?: string;
    partId?: string;
    limit?: number;
    offset?: number;
  } = {},
): Promise<ProgramListResponse> {
  const { data } = await apiClient.get<ProgramListResponse>(
    "/v1/nc-review/programs",
    {
      params: {
        status: params.status,
        machine_id: params.machineId,
        part_id: params.partId,
        limit: params.limit ?? 50,
        offset: params.offset ?? 0,
      },
    },
  );
  return data;
}

export async function getProgram(
  programId: string,
  opts?: { silent?: boolean },
): Promise<ProgramDetail> {
  const { data } = await apiClient.get<ProgramDetail>(
    `/v1/nc-review/programs/${programId}`,
    opts?.silent ? { _silentError: true } : undefined,
  );
  return data;
}

/**
 * V88 (2026-05-27) · 软删 NC 程序 · 返 204 No Content.
 * 后端设 deleted_at=now() · 物理保留 (audit/optimize_jobs 关联不破).
 * 列表会自动隐藏 · 调用方 refresh list 即可.
 * 幂等: 重复 DELETE 同 program 仍返 204 不报错.
 */
export async function deleteProgram(programId: string): Promise<void> {
  await apiClient.delete(`/v1/nc-review/programs/${programId}`, {
    _errorContext: "归档程序失败",
  });
}

export async function getReport(
  programId: string,
  opts?: { silent?: boolean },
): Promise<ReportData> {
  const { data } = await apiClient.get<ReportData>(
    `/v1/nc-review/programs/${programId}/report`,
    opts?.silent ? { _silentError: true } : undefined,
  );
  return data;
}

/**
 * 生成/重算风险报告 · 生成程序回填 (生成流水线此前不评估 → 详情页空报告) 或已有报告重算.
 * 多工位走分段子集 (运动几何规则除外 · rule_set_version=v0.1-ms). 返回与 getReport 同形.
 * 409=已签字不可重算 · 422=单工位解析失败.
 * 原始文件读不回来: 404 SOURCE_OBJECT_MISSING=文件已不在存储里 (重试无效 · message 会显示给用户) ·
 * 503 SOURCE_STORAGE_UNAVAILABLE=存储暂时读不了 (稍后重试).
 */
export async function analyzeProgram(programId: string): Promise<ReportData> {
  const { data } = await apiClient.post<ReportData>(
    `/v1/nc-review/programs/${programId}/analyze`,
    undefined,
    { _errorContext: "生成风险报告失败" },
  );
  return data;
}

export async function reviewProgram(
  programId: string,
  decision: "approve" | "reject",
  remarks: string,
): Promise<ReviewResult> {
  const { data } = await apiClient.post<ReviewResult>(
    `/v1/nc-review/programs/${programId}/review`,
    { decision, remarks },
    { _errorContext: decision === "approve" ? "签字失败" : "驳回失败" },
  );
  return data;
}

// ─── 优化 API (复用 standalone OptimizationPipeline) ────────────

/** ⑤ · 变更归类 · 前端分区展示 (parameter=切削参数 / path / advisory=建议性). */
export type OptimizeChangeCategory = "parameter" | "path" | "advisory" | "";

export interface OptimizeChange {
  line: number;
  kind: string;
  before: string;
  after: string;
  reason: string;
  est_time_saved_seconds: number;
  /**
   * ⑤ · 结构化 why + 置信度. 按 kind 不同 key 不同:
   * - spindle_speed: rpm_old/rpm_new/rule_baseline_rpm/vc_mpm
   * - feed_rate: f_old/f_new/multiplier/segment_length_mm/mrr_seg/mrr_target
   * - cycle_param: q_old/q_new/depth_mm
   * 通用: confidence / rule_agreement / final_confidence (有 rule baseline 时).
   * 旧缓存/旧 job 无此字段 → undefined · 前端降级.
   */
  params?: Record<string, number | string>;
  /** ⑤ · parameter / path / advisory · 旧数据缺失为 "". */
  category?: OptimizeChangeCategory;
}

/** ⑤ · 单刀具优化假设 (来自 optimize_context.build_process_context). */
export interface OptimizationToolContext {
  diameter_mm: number;
  flutes: number;
  tool_material: string | null;
  op_kind: string;
  cutting_params: {
    vc_min: number;
    vc_max: number;
    fz_min: number;
    fz_max: number;
  };
  current_rpm: number | null;
  current_rpm_line: number | null;
}

/** ⑤ · 优化假设上下文 · "优化假设面板"数据源 (material/iso/机床限/per-tool). */
export interface OptimizationContext {
  iso_group?: string;
  material?: string;
  machine?: Record<string, number | string | null>;
  feed_ceiling_mm_min?: number;
  /** 刀号(字符串化的 int)→ 刀具上下文. */
  tools?: Record<string, OptimizationToolContext>;
}

export type OptimizeEngineMode = "rule" | "llm" | "hybrid";

export interface AgentTurn {
  iteration: number;
  tool_name: string;
  arguments_summary: string;
  result_summary: string;
  ok: boolean;
  error_code: string | null;
  hint: string | null;
  latency_ms: number;
  // V49 (2026-05-24) · LLM round-trip · 上一轮 tool_result → 本轮 tool_call
  // 老数据 (V48 之前) 没此字段 · 用 ? 兼容
  llm_latency_ms?: number;
}

export interface OptimizeResult {
  program_id: string;
  filename: string;
  changes_count: number;
  est_time_saved_seconds: number;
  aborted: boolean;
  abort_reason: string | null;
  warnings: string[];
  changes: OptimizeChange[];
  optimized_object_key: string;
  optimized_filename: string;
  optimized_size_bytes: number;
  download_path: string | null;
  engine_used?: OptimizeEngineMode | "rule_via_llm_fallback";
  /** 改进 #2: 用户原始请求的 engine · 即使最终降级 attempted 也保留. */
  engine_attempted?: OptimizeEngineMode;
  /**
   * 改进 #1: 非空时说明为什么 engine_used != engine_attempted.
   * 形如 'llm_minimax_unreachable_after_5_retries' / 'llm_no_proposal' /
   * 'trivial_program' / 'rate_limited:N' / 'llm_aborted:...'
   */
  fallback_reason?: string | null;
  agent_summary?: string;
  agent_turns?: AgentTurn[];
  /** ⑤ · 优化假设上下文 (材料/刀具/机床) · 旧结果缺失为 undefined. */
  optimization_context?: OptimizationContext;
}

/**
 * V69 (2026-05-25) · 附件常量 · 前后端共享 · 改后端时这里同步.
 * - PREFIX 跟 program_optimize.py 的 OPTIMIZE_SESSION_PREFIX 一致
 * - MAX_BYTES 跟 chat_attachments.py 的 _MAX_FILE_SIZE_BYTES 一致
 * - MAX_COUNT 跟 file_reader.py 的 MAX_ATTACHMENTS_PER_REQUEST 一致
 */
export const OPTIMIZE_ATTACHMENT_SESSION_PREFIX = "optimize:";
export const MAX_ATTACHMENT_BYTES = 20 * 1024 * 1024;
export const MAX_ATTACHMENTS_PER_REQUEST = 10;

export interface OptimizeRequest {
  feed_ceiling_mm_min?: number;
  material?: "steel" | "aluminum" | "stainless" | "titanium" | "plastic";
  enable_feed_rate?: boolean;
  enable_cycle?: boolean;
  enable_travel?: boolean;
  enable_merge?: boolean;
  enable_coolant?: boolean;
  exclude_lines?: number[];
  engine_mode?: OptimizeEngineMode;
  /** 强制重跑 · 跳过 Redis cache 命中检查 (但仍 store 新结果). 默认 false. */
  force_refresh?: boolean;
  /**
   * V69 (2026-05-25) · 优化附件 · 图纸 PDF / 刀具表 Excel / 工艺说明 DOCX / 图片 JPG/PNG.
   * 前端先 POST /v1/chat/attachments (session_id='optimize:<program_id>') 拿 attachment_id.
   * 图片走 Anthropic vision · 文档让 LLM 主动调 read_*_document.
   * V69 code-review · 复用 ChatAttachment 类型 · 防 inline 类型 silent drift.
   */
  attachments?: import("./chat").ChatAttachment[];
}

export interface OptimizeJobAccepted {
  job_id: string;
  status: "pending";
  engine_mode: OptimizeEngineMode;
  status_url: string;
  celery_task_id?: string;
}

/** 方案 1 SSE 进度推送 (2026-05-20) · 后端 _compute_progress 翻译 tool_call. */
export interface OptimizeProgress {
  /** orient / baseline / inspect / propose / validate / finalize / starting / running */
  phase: string;
  /** 0-100 · 单调不降 (LLM 回到 inspect 阶段时停而不退) */
  pct: number;
  /** 中文友好消息 · 形如 "提议进给率调整" "查询材料工艺参数" */
  message: string;
  /** LLM 已调用工具总数 */
  tool_count: number;
  /** LLM 已 propose 改动数 (propose_* 工具计数) */
  proposal_count: number;
  /** 当前正在调用的工具名 · 形如 "propose_feed_rate_change" */
  current_tool?: string;
}

export interface OptimizeJobPartial {
  agent_turns: AgentTurn[];
  progress_turn_count: number;
  in_progress: boolean;
  /** 后端 _flush_partial 写进 result_json.progress · 前端用来展示真实阶段. */
  progress?: OptimizeProgress;
}

export interface OptimizeJobStatus {
  job_id: string;
  program_id: string;
  status: "pending" | "running" | "completed" | "failed";
  engine_mode: OptimizeEngineMode;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
  /** status=completed 时含完整结果. */
  result?: OptimizeResult;
  /** status=running 时含 partial trajectory (worker 限频 1Hz 写). */
  partial?: OptimizeJobPartial;
  error?: string;
}

/** 同步路径 (rule 引擎) · 返回完整结果. */
export async function optimizeProgram(
  programId: string,
  params: OptimizeRequest = {},
): Promise<OptimizeResult> {
  const { data } = await apiClient.post<OptimizeResult>(
    `/v1/nc-review/programs/${programId}/optimize`,
    params,
    { _errorContext: "优化失败" },
  );
  return data;
}

/** 异步路径 (llm / hybrid 引擎) · 返回 job_id, 由 pollOptimizeJob 轮询. */
export async function optimizeProgramAsync(
  programId: string,
  params: OptimizeRequest,
): Promise<OptimizeJobAccepted> {
  const { data } = await apiClient.post<OptimizeJobAccepted>(
    `/v1/nc-review/programs/${programId}/optimize`,
    params,
    { _errorContext: "优化任务提交失败" },
  );
  return data;
}

/** 单次查询 job 状态. */
export async function getOptimizeJob(
  programId: string,
  jobId: string,
): Promise<OptimizeJobStatus> {
  const { data } = await apiClient.get<OptimizeJobStatus>(
    `/v1/nc-review/programs/${programId}/optimize-jobs/${jobId}`,
  );
  return data;
}

/** 轮询至 completed/failed · 默认 2s 一次, 最多 max_seconds. */
export async function pollOptimizeJob(
  programId: string,
  jobId: string,
  options: {
    onProgress?: (status: OptimizeJobStatus) => void;
    intervalMs?: number;
    maxSeconds?: number;
  } = {},
): Promise<OptimizeJobStatus> {
  const { onProgress, intervalMs = 2000, maxSeconds = 90 } = options;
  const startedAt = Date.now();
  while (true) {
    const status = await getOptimizeJob(programId, jobId);
    onProgress?.(status);
    if (status.status === "completed" || status.status === "failed") {
      return status;
    }
    if ((Date.now() - startedAt) / 1000 >= maxSeconds) {
      return status; // 超时返当前态, 调用方判断 pending/running
    }
    await new Promise((r) => setTimeout(r, intervalMs));
  }
}

export async function downloadOptimizedProgram(
  programId: string,
): Promise<Blob> {
  const { data } = await apiClient.get<Blob>(
    `/v1/nc-review/programs/${programId}/optimized/download`,
    { responseType: "blob", _errorContext: "下载失败" },
  );
  return data;
}

/**
 * 下载原始 .nc 文件 (优化前) · diff 左侧基线
 *
 * 返回 text/plain 字符串 (UTF-8 优先, 失败回退替换字符).
 * 读不回来时和 analyzeProgram 一样: 404 SOURCE_OBJECT_MISSING / 503 SOURCE_STORAGE_UNAVAILABLE.
 */
export async function downloadProgramSource(
  programId: string,
): Promise<string> {
  const { data } = await apiClient.get<Blob>(
    `/v1/nc-review/programs/${programId}/source/download`,
    { responseType: "blob", _errorContext: "下载原文失败" },
  );
  return await blobToText(data);
}

/**
 * 按 object_key 下载历史优化版本 · diff 右侧候选
 *
 * key 必须来自 /optimize/history 的 optimized_object_key 字段,
 * 后端会强校验 tenant_id + program_id 前缀.
 */
export async function downloadOptimizedByKey(
  programId: string,
  objectKey: string,
): Promise<string> {
  const { data } = await apiClient.get<Blob>(
    `/v1/nc-review/programs/${programId}/optimized/download-by-key`,
    {
      params: { key: objectKey },
      responseType: "blob",
      _errorContext: "历史版本下载失败",
    },
  );
  return await blobToText(data);
}

async function blobToText(blob: Blob): Promise<string> {
  // CNC 程序常见 3 种编码: UTF-8 (新软件 / 优化器输出) · GBK / GB18030 (中国 Windows 默认).
  // 后端 MinIO 透传原始字节, 编码识别在客户端做.
  const buf = await blob.arrayBuffer();

  // 1. 严格 UTF-8 · 含 BOM 的程序最常见
  try {
    return new TextDecoder("utf-8", { fatal: true }).decode(buf);
  } catch {
    /* UTF-8 严格解码失败 → 走 GBK 路径 */
  }

  // 2. GBK 兜底 (浏览器原生支持 gbk / gb18030, 含全部 GBK + 4 字节扩展)
  try {
    return new TextDecoder("gb18030").decode(buf);
  } catch {
    /* GBK 也失败 (极罕见, 老旧浏览器) → 走宽松 UTF-8 */
  }

  // 3. 兜底 · 宽松 UTF-8 (非法字节出 U+FFFD, 至少能看到 ASCII 部分)
  return new TextDecoder("utf-8", { fatal: false }).decode(buf);
}

// ─── 闭环优化-仿真验证 API (M7 · optimize↔simulate↔回喂 收敛) ────────────

export interface ClosedLoopRequest {
  /** 封顶轮数 1-10 · 默认 3. */
  max_rounds?: number;
  /** 优化引擎 · 默认 hybrid. */
  engine_mode?: OptimizeEngineMode;
  /** 几何仿真精度 (8-256) · null/省略 = 引擎默认. */
  sections?: number | null;
}

export interface ClosedLoopAccepted {
  loop_id: string;
  status: "running";
  max_rounds: number;
  status_url: string;
}

export type ClosedLoopStatus =
  | "running"
  | "converged"
  | "loop_failed"
  | "failed"
  // 无毛坯/CAD 几何 · 诚实降级:单轮规则优化完成 · 未做几何仿真验证
  | "rule_optimized";

/** 闭环每轮摘要 (loop 状态 GET 返回 · 明细走 getClosedLoopRoundReport). */
export interface ClosedLoopRoundSummary {
  run_id: string;
  round_no: number;
  status: string; // pending / running / completed / failed
  passed: boolean | null;
  high_count: number | null;
  cut_mesh_key: string | null;
  report_url: string;
  mesh_url: string | null;
}

/** 闭环某轮 optimize job 的 agent 轨迹 (与 sim rounds 解耦 · 进行中的轮实时增长). */
export interface ClosedLoopAgentRound {
  round_no: number;
  optimize_job_id: string;
  status: string; // pending / running / completed / failed
  agent_turns: AgentTurn[];
  agent_summary: string;
  /** true = job 仍在跑 · agent_turns 是 partial flush 的增量快照. */
  in_progress: boolean;
  /** ⑤/5b · 该轮参数改动 (F/S before→after + params) · 旧 job 缺失为 []. */
  changes?: OptimizeChange[];
  changes_count?: number;
  /** ⑤/5b · 该轮估算节省工时 (秒) · 节拍汇总用. */
  est_time_saved_seconds?: number;
}

export interface ClosedLoopState {
  loop_id: string;
  program_id: string;
  status: ClosedLoopStatus;
  converged: boolean;
  current_round: number;
  max_rounds: number;
  /** 收敛后镜像 nc_program.review_status (pending = 待人工签核). */
  final_review_status: string | null;
  error_text: string | null;
  /** 每轮优化 agent 轨迹 (镜像 GenerateState.trajectory · 生成页/进度页实时渲染). */
  agent_rounds?: ClosedLoopAgentRound[];
  rounds: ClosedLoopRoundSummary[];
}

export type SimProblemCategory =
  | "collision"
  | "overcut"
  | "undercut"
  | "air_cut"
  | "excessive_load" // ⑤/5b · RFC-013 Tier-2 · 切削负载过载 (MRR > 3× 程序中位)
  | "sim_degraded";

/** ⑤/5b · RFC-013 Tier-2 负载剖面汇总 (LoadProfile.summary). 力/功率为估算值·非实测. */
export interface LoadSummary {
  cutting_segments?: number;
  median_mrr_mm3_min?: number | null;
  max_mrr_mm3_min?: number | null;
  /** 力/功率为估算值·非实测 (仿真期 ap/rpm/Kc 常缺 → null). */
  max_power_kw?: number | null;
  max_chip_thickness_mm?: number | null;
}

/** ⑤/5b · 逐 NC 行负载段 (compact_load_segments). */
export interface LoadSegment {
  line: number;
  feedrate?: number | null;
  mrr?: number | null;
  length?: number | null;
}
export type SimProblemSeverity = "high" | "medium" | "low";

/** 单条仿真问题 · 跟后端 detectors.SimProblem.to_dict 一致. */
export interface SimProblem {
  category: SimProblemCategory;
  severity: SimProblemSeverity;
  /** NC 行号 · 碰撞/空切有 · 过切/欠切为程序级=null. */
  line: number | null;
  location_xyz: [number, number, number] | null;
  detail: string;
  /** 量化 (过切/欠切体积 mm³ 等). */
  metric_mm3: number | null;
}

/** 某轮完整报告 · 跟后端 get_closed_loop_round_report_endpoint 一致. */
export interface ClosedLoopRoundReport {
  loop_id: string;
  program_id: string;
  run_id: string;
  round_no: number;
  status: string;
  passed: boolean | null;
  high_count: number | null;
  cut_mesh_key: string | null;
  /** 该轮 optimize job_id · 拉每轮优化 trace 用 (worker 未建时 null). */
  optimize_job_id: string | null;
  problems: SimProblem[];
  removed_volume_mm3: number | null;
  overcut_mm3: number | null;
  undercut_mm3: number | null;
  boolean_failures: number | null;
  /** ⑤/5b · 负载剖面 · 旧 run (RFC-013 Tier-2 前) 为 null. */
  load_summary?: LoadSummary | null;
  load_segments?: LoadSegment[] | null;
  mesh_url: string | null;
}

/** 启动闭环 · 返回 loop_id, 由 pollClosedLoop 轮询. */
export async function startClosedLoop(
  programId: string,
  params: ClosedLoopRequest = {},
): Promise<ClosedLoopAccepted> {
  const { data } = await apiClient.post<ClosedLoopAccepted>(
    `/v1/nc-review/programs/${programId}/closed-loop`,
    params,
    { _errorContext: "闭环启动失败" },
  );
  return data;
}

/** 单次查询闭环状态 + 每轮摘要. */
export async function getClosedLoopStatus(
  programId: string,
  loopId: string,
): Promise<ClosedLoopState> {
  const { data } = await apiClient.get<ClosedLoopState>(
    `/v1/nc-review/programs/${programId}/closed-loop/${loopId}`,
  );
  return data;
}

/** 某轮完整仿真报告 (problems[] 明细 + optimize job_id). */
export async function getClosedLoopRoundReport(
  programId: string,
  loopId: string,
  roundNo: number,
): Promise<ClosedLoopRoundReport> {
  const { data } = await apiClient.get<ClosedLoopRoundReport>(
    `/v1/nc-review/programs/${programId}/closed-loop/${loopId}/rounds/${roundNo}/report`,
  );
  return data;
}

/** 拉 cut-mesh STL 字节 (带 JWT · 交给 three.js STLLoader.parse · 见 FE-6). */
export async function fetchClosedLoopMesh(
  programId: string,
  runId: string,
): Promise<ArrayBuffer> {
  const { data } = await apiClient.get<ArrayBuffer>(
    `/v1/nc-review/programs/${programId}/simulations/${runId}/mesh`,
    { responseType: "arraybuffer", _errorContext: "切削网格下载失败" },
  );
  return data;
}

export type PartMeshKind = "target" | "stock" | "source";

/** RFC-010 F1 · 拉零件几何 STL (3D 预览) · kind: target(成品)/stock(毛坯)/source(STEP惰转). */
export async function fetchPartMesh(
  partId: string,
  kind: PartMeshKind = "target",
): Promise<ArrayBuffer> {
  const { data } = await apiClient.get<ArrayBuffer>(
    `/v1/nc-review/parts/${partId}/mesh`,
    {
      params: { kind },
      headers: { "Cache-Control": "no-cache" },
      responseType: "arraybuffer",
      // 全局静默:3D 预览的 404(无几何)/413(过大)/503(镶嵌不可用)由 StepMeshViewer
      // 就地优雅降级(显"暂无几何"/"几何过大"等)· 不弹全局错误 toast(大装配惰转失败时尤其扰民)。
      _silentError: true,
    },
  );
  return data;
}

export type DrawingOrthoView = "front" | "top" | "right" | "iso";
export type DrawingSectionView = "section-x" | "section-y" | "section-z";
export type DrawingView = DrawingOrthoView | DrawingSectionView;
export type DrawingFormat = "svg" | "dxf" | "pdf";

/** view=section-* 拼剖切位(M4a) · dims 拼自动标注开关(M4b · 仅 svg). */
function drawingParams(
  view: DrawingView,
  format: DrawingFormat,
  sectionOffset?: number,
  dims?: boolean,
): Record<string, string | number | boolean> {
  const params: Record<string, string | number | boolean> = { view, format };
  if (view.startsWith("section-") && sectionOffset !== undefined) {
    params.section_offset = sectionOffset;
  }
  if (dims && format === "svg") {
    params.dims = true;
  }
  return params;
}

/** RFC-010 M4b · 拉工程图总图 SVG(多视图+尺寸+标题栏+技术要求 · withAi=含 AI 标注栏 · ~30s). */
export async function fetchPartDrawingSheet(
  partId: string,
  withAi: boolean,
): Promise<string> {
  const { data } = await apiClient.get<Blob>(
    `/v1/nc-review/parts/${partId}/drawing/sheet`,
    {
      params: withAi ? { ai: true } : {},
      responseType: "blob",
      _errorContext: "工程图总图加载失败",
    },
  );
  return await blobToText(data);
}

// ─── RFC-010 S1 · AI 标注风格学习闭环(复核面板) ───

export interface AiDimensionScheme {
  dimensions: Record<string, unknown>[];
  gdt: Record<string, unknown>[];
  datums: Record<string, unknown>[];
  surface_finish: Record<string, unknown>[];
  threads: Record<string, unknown>[];
  notes: string[];
  source: string;
}

export interface AiDimensionsResponse {
  proposal_id: string | null;
  candidates: Record<string, unknown>;
  scheme: AiDimensionScheme;
}

export interface AnnoItemDecision {
  kind: string;
  action: "accept" | "edit" | "reject" | "add";
  ai: Record<string, unknown> | null;
  final: Record<string, unknown> | null;
}

export interface AnnoFeedbackBody {
  proposal_id: string;
  final_scheme: Record<string, unknown>;
  item_decisions: AnnoItemDecision[];
}

/** RFC-010 S1 · 拉 AI 荐标注方案(结构化 · 含 proposal_id 供复核引用 · ~30s). */
export async function fetchPartAiDimensions(
  partId: string,
): Promise<AiDimensionsResponse> {
  const { data } = await apiClient.get<AiDimensionsResponse>(
    `/v1/nc-review/parts/${partId}/drawing/ai-dimensions`,
    { _errorContext: "AI 荐标注加载失败" },
  );
  return data;
}

/** RFC-010 S1 · 老师傅复核 AI 标注(逐条 accept/edit/reject)→ 算 accept_rate + 喂飞轮. */
export async function postAnnoFeedback(
  partId: string,
  body: AnnoFeedbackBody,
): Promise<{ feedback_id: string; accept_rate: number | null }> {
  const { data } = await apiClient.post<{
    feedback_id: string;
    accept_rate: number | null;
  }>(`/v1/nc-review/parts/${partId}/drawing/anno-feedback`, body, {
    _errorContext: "复核提交失败",
  });
  return data;
}

/** RFC-010 M3/M4a/M4b · 拉单视图 SVG 工程图(inline 预览 · 含剖视 · dims=自动标注 · 返 SVG 文本). */
export async function fetchPartDrawingSvg(
  partId: string,
  view: DrawingView = "front",
  sectionOffset?: number,
  dims?: boolean,
): Promise<string> {
  const { data } = await apiClient.get<Blob>(
    `/v1/nc-review/parts/${partId}/drawing`,
    {
      params: drawingParams(view, "svg", sectionOffset, dims),
      responseType: "blob",
      _errorContext: "工程图加载失败",
    },
  );
  return await blobToText(data);
}

/** RFC-010 M3/M4a · 下载工程图 · dxf(单视图/剖视 · 车间)/ pdf(整页 A3 4 视图 · 忽略 view). */
export async function downloadPartDrawing(
  partId: string,
  view: DrawingView,
  format: "dxf" | "pdf",
  sectionOffset?: number,
): Promise<Blob> {
  const { data } = await apiClient.get<Blob>(
    `/v1/nc-review/parts/${partId}/drawing`,
    {
      params: drawingParams(view, format, sectionOffset),
      responseType: "blob",
      _errorContext: "工程图下载失败",
    },
  );
  return data;
}

// ─── RFC-011 · 独立拆图工作台 CAD 文档(解耦 NC 程序生成) ───

export type CadConvertStatus =
  | "pending"
  | "converted"
  | "unsupported"
  | "failed"
  | "mesh_preview";

export interface CadDocumentUploaded {
  document_id: string;
  status: string;
  format: string;
  kind: "part" | "assembly";
  convert_status: CadConvertStatus;
  /**
   * 零件文件里的实体数(装配体 / 读不了为 null)。> 1 = 多实体件(或拍平的装配):
   * 按一个零件报会把几个实体合计,向导据此让人选拆不拆(报价计划 M5 第 2 条)
   */
  body_count?: number | null;
}

export interface CadDocumentMeta {
  id: string;
  filename: string;
  format: string;
  kind: "part" | "assembly";
  convert_status: CadConvertStatus;
  part_count: number;
  project_id: string | null;
  created_at: string | null;
  can_draw: boolean;
  can_preview: boolean;
}

/** RFC-011 M1 · 上传任意 CAD → 建 cad_document(校验/杀毒/去重/归一 · STEP 直通即可出图). */
export async function uploadCadDocument(
  file: File,
  projectId?: string,
): Promise<CadDocumentUploaded> {
  const form = new FormData();
  form.append("file", file);
  if (projectId) form.append("project_id", projectId);
  const { data } = await apiClient.post<CadDocumentUploaded>(
    `/v1/nc-review/cad-documents`,
    form,
    { _errorContext: "CAD 上传失败" },
  );
  return data;
}

/** RFC-011 M1 · CAD 文档元数据(含 can_draw 标志). */
export async function fetchCadDocument(
  documentId: string,
): Promise<CadDocumentMeta> {
  const { data } = await apiClient.get<CadDocumentMeta>(
    `/v1/nc-review/cad-documents/${documentId}`,
    { _errorContext: "CAD 文档加载失败" },
  );
  return data;
}

/** RFC-011 M1 · 拉 CAD 文档 3D 预览 STL(惰性镶嵌 + 缓存). */
export async function fetchCadDocumentMesh(
  documentId: string,
): Promise<ArrayBuffer> {
  const { data } = await apiClient.get<ArrayBuffer>(
    `/v1/nc-review/cad-documents/${documentId}/mesh`,
    {
      responseType: "arraybuffer",
      _silentError: true, // 预览失败由 StepMeshViewer 内联优雅降级 · 不弹 toast
      _errorContext: "CAD 网格下载失败",
    },
  );
  return data;
}

/** RFC-011 M1 · 拉 CAD 文档单视图 SVG 工程图(复用 RFC-010 HLR · inline 预览). */
export async function fetchCadDocumentDrawingSvg(
  documentId: string,
  view: DrawingView = "front",
  sectionOffset?: number,
  dims?: boolean,
): Promise<string> {
  const { data } = await apiClient.get<Blob>(
    `/v1/nc-review/cad-documents/${documentId}/drawing`,
    {
      params: drawingParams(view, "svg", sectionOffset, dims),
      responseType: "blob",
      _silentError: true, // 出图失败由页面内联提示 · 不弹 toast
      _errorContext: "工程图加载失败",
    },
  );
  return await blobToText(data);
}

/** RFC-011 M1 · 下载 CAD 文档工程图 · dxf(单视图/剖视)/ pdf(整页 4 视图). */
export async function downloadCadDocumentDrawing(
  documentId: string,
  view: DrawingView,
  format: "dxf" | "pdf",
  sectionOffset?: number,
): Promise<Blob> {
  const { data } = await apiClient.get<Blob>(
    `/v1/nc-review/cad-documents/${documentId}/drawing`,
    {
      params: drawingParams(view, format, sectionOffset),
      responseType: "blob",
      _errorContext: "工程图下载失败",
    },
  );
  return data;
}

/** RFC-011 #4 · 下载「所见即所得」的标注总图 PDF(复用屏幕上的 compose_sheet_svg · ai 尊重开关).
 *
 * 与 downloadCadDocumentDrawing(format=pdf · 老 4 视图图)不同 —— 此处导出的就是屏幕预览的
 * 同一张总图(含自动测量尺寸 / 孔表 / 剖视 / 技术要求 / ai=true 时的 AI 标注栏)。150s 超时同 sheet。
 */
export async function downloadCadSheetPdf(
  documentId: string,
  ai = true,
  processKind = "none",
  dimStrategy = "chain",
): Promise<Blob> {
  const params: Record<string, string | number | boolean> = {
    ai,
    v: 3,
    format: "pdf",
  };
  if (processKind !== "none") params.process_kind = processKind;
  if (dimStrategy !== "chain") params.dim_strategy = dimStrategy;
  const { data } = await apiClient.get<Blob>(
    `/v1/nc-review/cad-documents/${documentId}/drawing/sheet`,
    {
      params,
      responseType: "blob",
      timeout: 360_000, // 同 sheet:AI 预算 32k/240s + 渲染(见 drawingScheme.ts)
      _errorContext: "PDF 下载失败",
    },
  );
  return data;
}

/** 整机/单件 STEP(=STP · 同一格式)下载 · 后端从 MinIO 取 converted_step_key 真文件。
 *  生成件可能体量大(整机 STEP 数十 MB)→ 放宽 timeout。 */
export async function downloadCadStep(documentId: string): Promise<Blob> {
  const { data } = await apiClient.get<Blob>(
    `/v1/nc-review/cad-documents/${documentId}/step`,
    { responseType: "blob", timeout: 180_000, _errorContext: "STEP 下载失败" },
  );
  return data;
}

/** 3D 网格 STL 下载 · 复用 /mesh 端点(STEP 镶嵌 · 服务端首请求生成后缓存)。 */
export async function downloadCadStl(documentId: string): Promise<Blob> {
  const { data } = await apiClient.get<Blob>(
    `/v1/nc-review/cad-documents/${documentId}/mesh`,
    { responseType: "blob", timeout: 180_000, _errorContext: "STL 下载失败" },
  );
  return data;
}

/** BOM 导出(csv | xlsx | json)· 后端从 bom_json + synth_meta semantic_bom 出真清单。 */
export async function downloadCadBom(
  documentId: string,
  format: "csv" | "xlsx" | "json" = "xlsx",
): Promise<Blob> {
  const { data } = await apiClient.get<Blob>(
    `/v1/nc-review/cad-documents/${documentId}/bom`,
    {
      params: { format },
      responseType: "blob",
      timeout: 60_000,
      _errorContext: "BOM 下载失败",
    },
  );
  return data;
}

/** RFC-011 #4 · 单件工程图总图 SVG · 默认自带 AI 智能标注(ai=true · LLM 荐尺寸 · 需复核).
 *
 * ``timeoutMs`` 默认 150s(放宽 axios 实例默认 60s):AI 总图 = extract_candidates(多视图 HLR)
 * + LLM 荐尺寸(实测单次 ~13s · 含重试) + 服务端渲染(45s 上限)· 复杂有机件总耗时常 >60s ·
 * 默认 60s 会抢在服务端完成/优雅 422 前抢断 → 报 "[HTTP —] timeout"。150s 让其完成或拿到 422。
 */
export async function fetchCadDocumentSheet(
  documentId: string,
  ai = true,
  processKind = "none",
  dimStrategy = "chain",
  refresh = false,
  timeoutMs = 150_000,
): Promise<string> {
  // v=2 · 剖视尺寸去挤渲染版本 · 换 URL 绕浏览器缓存(与服务端 cache_name -v2 配对)
  const params: Record<string, string | number | boolean> = { ai, v: 3 };
  if (processKind !== "none") params.process_kind = processKind; // RFC-012 M2 · 工艺驱动标注
  if (dimStrategy !== "chain") params.dim_strategy = dimStrategy; // RFC-012 M3 · 尺寸策略
  if (refresh) {
    params.refresh = true; // 跳服务端缓存 · 强制重渲
    params.t = Date.now(); // 同时绕浏览器 HTTP 缓存(URL 唯一)
  }
  const { data } = await apiClient.get<Blob>(
    `/v1/nc-review/cad-documents/${documentId}/drawing/sheet`,
    {
      params,
      responseType: "blob",
      timeout: timeoutMs,
      _silentError: true,
      _errorContext: "工程图总图加载失败",
    },
  );
  return await blobToText(data);
}

/** 出图确认门(M4)· sheet + 方案状态(X-Drawing-Scheme-Status:none|confirmed)。
 *  chat 出图卡用:confirmed = 该图走的是人工确认后的正式方案(显「已确认」角标)。 */
export async function fetchCadSheetWithStatus(
  documentId: string,
  partIndex: number | null,
  ai = true,
  processKind = "none",
  dimStrategy = "chain",
  refresh = false,
  timeoutMs = 280_000,
): Promise<{ svg: string; schemeStatus: "none" | "confirmed" }> {
  const params: Record<string, string | number | boolean> = { ai, v: 3 };
  if (processKind !== "none") params.process_kind = processKind;
  if (dimStrategy !== "chain") params.dim_strategy = dimStrategy;
  if (refresh) {
    params.refresh = true;
    params.t = Date.now();
  }
  const url =
    partIndex != null && partIndex >= 0
      ? `/v1/nc-review/cad-documents/${documentId}/assembly/parts/${partIndex}/drawing/sheet`
      : `/v1/nc-review/cad-documents/${documentId}/drawing/sheet`;
  const resp = await apiClient.get<Blob>(url, {
    params,
    responseType: "blob",
    timeout: timeoutMs,
    _silentError: true,
    _errorContext: "工程图总图加载失败",
  });
  const status =
    resp.headers["x-drawing-scheme-status"] === "confirmed"
      ? "confirmed"
      : "none";
  return { svg: await blobToText(resp.data), schemeStatus: status };
}

// ─── Phase 2 · VLM 自检改单 (advisory · /drawing/qc-review) ───

/** VLM 自检改单一条 · 与后端 vlm_review.QcFinding 对齐. */
export interface CadQcFinding {
  type: string;
  where: string;
  detail: string;
  fix: string;
}

/** VLM 自检结果 · 与后端 /drawing/qc-review 响应对齐. */
export interface CadQcReview {
  ok: boolean;
  provider: string;
  findings: CadQcFinding[];
}

/**
 * VLM 审图自检(advisory · 视觉模型按 GB/T 找标注问题 → 改单 · 不自动改图)。
 * 慢(minimax 主 ~1-4min · anthropic 兜底 ~30s)· 结果后端按 (件,参数) 缓存 · 二调秒回。
 */
export async function fetchCadQcReview(
  documentId: string,
  ai = true,
  processKind = "none",
  dimStrategy = "chain",
  timeoutMs = 300_000,
): Promise<CadQcReview> {
  const params: Record<string, string | number | boolean> = { ai };
  if (processKind !== "none") params.process_kind = processKind;
  if (dimStrategy !== "chain") params.dim_strategy = dimStrategy;
  const { data } = await apiClient.get<CadQcReview>(
    `/v1/nc-review/cad-documents/${documentId}/drawing/qc-review`,
    {
      params,
      timeout: timeoutMs,
      _silentError: true,
      _errorContext: "AI 自检失败",
    },
  );
  return data;
}

// ─── Phase 2 · W2 · VLM 审图重渲闭环 (/drawing/qc-improve) ───

/** VLM 重渲闭环结果 · 与后端 /drawing/qc-improve 响应对齐. */
export interface CadQcImprove {
  ok: boolean; // VLM 真跑过(false=降级 · 未改图)
  changed: boolean; // 有无自动补全(决定上方图是否已是改进图)
  rounds_run: number;
  applied: string[]; // 已自动补的漏标尺寸(值来自几何精测 · 均标"需复核")
  advisory: CadQcFinding[]; // 未自动改 · 交人工(公差/重叠/基准/视图)
  svg: string; // base64(UTF-8 SVG) · 改进后(或无改动时为初始)总图
}

/**
 * VLM 审图重渲闭环(自检 → 漏标且值对上几何的尺寸确定性补全 → 重渲)· 返回改进图 + 审计。
 * 安全边界:只补漏标几何值(标"需复核")· 公差/重叠/基准/视图一律 advisory。
 * 慢(同 qc-review · minimax 主 ~1-4min · anthropic 兜底)· 结果后端按 (件,参数) 缓存 · 二调秒回。
 */
export async function fetchCadQcImprove(
  documentId: string,
  ai = true,
  processKind = "none",
  dimStrategy = "chain",
  timeoutMs = 300_000,
): Promise<CadQcImprove> {
  const params: Record<string, string | number | boolean> = { ai };
  if (processKind !== "none") params.process_kind = processKind;
  if (dimStrategy !== "chain") params.dim_strategy = dimStrategy;
  const { data } = await apiClient.get<CadQcImprove>(
    `/v1/nc-review/cad-documents/${documentId}/drawing/qc-improve`,
    {
      params,
      timeout: timeoutMs,
      _silentError: true,
      _errorContext: "AI 自检并改进失败",
    },
  );
  return data;
}

// ─── 统一质量评价 (七维100分+一票否决 · /drawing/quality-eval) ───

/** 单维得分 · 与后端 quality_eval.DimensionScore 对齐. */
export interface CadQualityDimension {
  code: string; // D1..D7
  name: string;
  max_score: number;
  score: number;
  auto_coverage: "full" | "partial" | "minimal";
  issues: { deduction: number; detail: string; source: string }[];
  needs_human: string[];
}

/** 一票否决项判定 · 与后端 quality_eval.VetoAssessment 对齐. */
export interface CadQualityVeto {
  code: string; // V1..V8
  name: string;
  status: "triggered" | "pass" | "needs_human";
  detail: string;
}

/** 统一质量评估报告 · 与后端 /drawing/quality-eval 响应对齐. */
export interface CadQualityReport {
  rubric_version: string;
  total: number;
  grade: "A" | "B" | "C" | "D";
  grade_action: string;
  release_ready_ai: boolean;
  human_review_required: boolean;
  vlm_status: "ok" | "failed" | "skipped";
  vlm_provider: string | null;
  dimensions: CadQualityDimension[];
  vetoes: CadQualityVeto[];
}

/**
 * 统一出图质量评估(七维100分+一票否决 · GB/T rubric · AI 初评 · 语义项需人工)。
 * vlm=true 时含视觉发现(与 qc-review 共享缓存 · 不重复烧 VLM)· 结果按 (件,参数,rubric版本) 缓存。
 */
export async function fetchCadQualityEval(
  documentId: string,
  ai = true,
  processKind = "none",
  dimStrategy = "chain",
  vlm = true,
  timeoutMs = 300_000,
): Promise<CadQualityReport> {
  const params: Record<string, string | number | boolean> = { ai, vlm };
  if (processKind !== "none") params.process_kind = processKind;
  if (dimStrategy !== "chain") params.dim_strategy = dimStrategy;
  const { data } = await apiClient.get<CadQualityReport>(
    `/v1/nc-review/cad-documents/${documentId}/drawing/quality-eval`,
    {
      params,
      timeout: timeoutMs,
      _silentError: true,
      _errorContext: "质量评分失败",
    },
  );
  return data;
}

// ─── ③ 后处理 · CAD 文档材料 + 后处理工序 ───

/** 一项后处理工序 · 与后端 drawing/post_process.PostProcessSpec 对齐. */
export interface CadPostProcessSpec {
  kind: string; // surface_treat / heat_treat / surface_strengthen / deburr / surface_finish / coating / weld_post
  method?: string;
  spec?: string;
  thickness_um?: number; // 仅 coating
  dim_state?: "as_machined" | "before_treat" | "after_treat"; // 仅 coating
}

export interface CadDocMeta {
  material: string | null;
  post_process: CadPostProcessSpec[] | null;
}

/** ③ · 设文档材料 + 后处理工序(出图技术要求/标题栏/尺寸基准据此)· 后端校验丢未知 kind. */
export async function setCadDocumentMeta(
  documentId: string,
  body: {
    material?: string | null;
    post_process?: CadPostProcessSpec[] | null;
  },
): Promise<CadDocMeta & { document_id: string }> {
  const { data } = await apiClient.patch<CadDocMeta & { document_id: string }>(
    `/v1/nc-review/cad-documents/${documentId}/meta`,
    body,
  );
  return data;
}

// ─── RFC-012 M3 · 批量审核流 ───

export interface BatchItemView {
  id: string;
  document_id: string;
  part_index: number | null;
  /** ④ · 装配逐件的零件名(后端从 cad_document.bom_json 按 part_index 解析)· 单件为 null. */
  part_name: string | null;
  confidence: number | null;
  band: string | null;
  review_status: string;
}

export interface DrawingBatchView {
  batch_id: string;
  name: string;
  status?: string;
  item_count: number;
  items: BatchItemView[];
}

export interface BatchItemInput {
  document_id: string;
  part_index?: number | null;
  confidence?: number | null;
  band?: string | null;
}

/** 创建批量审核(逐件置信度由前端算后带上)。 */
export async function createDrawingBatch(
  name: string,
  items: BatchItemInput[],
): Promise<DrawingBatchView> {
  const { data } = await apiClient.post<DrawingBatchView>(
    "/v1/nc-review/drawing-batches",
    {
      name,
      items,
    },
  );
  return data;
}

/** ⑤ 大装配 · 服务端从装配建批(每唯一件一行 · 置信度逐件懒填)· 需装配已拆解(bom_json)。 */
export async function createDrawingBatchFromAssembly(
  documentId: string,
  name = "",
): Promise<DrawingBatchView> {
  const { data } = await apiClient.post<DrawingBatchView>(
    `/v1/nc-review/drawing-batches/from-assembly/${documentId}`,
    { name },
    { _silentError: true, _errorContext: "装配建批失败" },
  );
  return data;
}

/** ⑤ 大装配异步预渲染建批受理(Temporal 后台逐件出图 + 算置信度)· 轮询 fetchDecomposeJob(job_id)。 */
export interface BatchFromAssemblyAsyncAccepted {
  batch_id: string;
  job_id: string;
  status: "running" | "failed";
  item_count: number;
  status_url: string;
}

/** ⑤ · 触发后台预渲染建批(Temporal 不可用 → status=failed · batch 仍建好可走客户端「全部出图」兜底)。 */
export async function createBatchFromAssemblyAsync(
  documentId: string,
  withAi = false,
  name = "",
  processKind = "none",
  dimStrategy = "chain",
): Promise<BatchFromAssemblyAsyncAccepted> {
  const { data } = await apiClient.post<BatchFromAssemblyAsyncAccepted>(
    `/v1/nc-review/drawing-batches/from-assembly-async/${documentId}`,
    // 出图策略必须随建批一起下去 —— 后台预渲染写的缓存键含策略段,派发时不传则整批
    // 预渲染出的是 chain 图,用户在工作台切策略只能靠逐件懒渲重出(白跑一遍预渲染)。
    { name, process_kind: processKind, dim_strategy: dimStrategy },
    {
      params: { with_ai: withAi },
      _silentError: true,
      _errorContext: "后台预渲染派发失败",
    },
  );
  return data;
}

/** ⑤ 懒审核 · 逐件出图后回填置信度(供"批量通过高置信"渐进可用)。 */
export async function setBatchItemConfidence(
  itemId: string,
  body: { confidence: number | null; band: string | null },
): Promise<BatchItemView> {
  const { data } = await apiClient.patch<BatchItemView>(
    `/v1/nc-review/drawing-batches/items/${itemId}/confidence`,
    body,
  );
  return data;
}

/** 拉批量进度 + 逐件。 */
export async function getDrawingBatch(
  batchId: string,
): Promise<DrawingBatchView> {
  const { data } = await apiClient.get<DrawingBatchView>(
    `/v1/nc-review/drawing-batches/${batchId}`,
  );
  return data;
}

/** 审核:无 itemIds → 批量通过高置信;有 itemIds → 逐件 approve/reject。 */
export async function approveDrawingBatch(
  batchId: string,
  body: { item_ids?: string[]; status?: "approved" | "rejected" },
): Promise<{
  approved_high_confidence?: number;
  reviewed?: number;
  status?: string;
}> {
  const { data } = await apiClient.post(
    `/v1/nc-review/drawing-batches/${batchId}/approve`,
    body,
  );
  return data as {
    approved_high_confidence?: number;
    reviewed?: number;
    status?: string;
  };
}

// ─── RFC-011 M3 · 装配拆图(BOM + 逐件工程图) ───

export interface CadBomPart {
  index: number;
  name: string;
  qty: number;
}

/** RFC-014 M2a · 装配里一个零件实例:引用哪个唯一件 + 世界摆位矩阵。 */
export interface CadAssemblyInstance {
  /** 引用的唯一件序号(= parts[].index)。 */
  u: number;
  /** 16 元行主序 4×4 世界摆位矩阵(把唯一件 master 摆到装配里的位置/姿态)。 */
  m: number[];
}

export interface CadAssembly {
  is_assembly: boolean;
  part_count: number;
  parts: CadBomPart[];
  /** RFC-014 M2a · 逐实例摆位 · 整机 InstancedMesh 渲染用 · 空 = 单件/旧文档 → 回退整机整体 mesh. */
  instances?: CadAssemblyInstance[];
  /** 人机参考模型(操作员假人)的 unique_index · 3D 预览默认隐藏(可开关显示)· 拆图已排除. */
  reference_unique_indices?: number[];
  /** RFC-015 · 逐唯一件有效颜色(index 对齐 · 用户色>分组色>null)· 查看器逐件着色. */
  part_colors?: (string | null)[];
  /** RFC-015 · 逐唯一件不透明度(0-1 · 透明安全门<1 看得见内部)· 默认 1. */
  part_opacities?: number[];
  /** RFC-015 · 逐唯一件爆炸偏移 [dx,dy,dz](mm · 按功能分组)· 爆炸视图用 · 默认 [0,0,0]. */
  part_explode?: number[][];
  /** MH · 功能分组图例 · 生成件非空 · 查看器画色块+中文名+件数 · 非生成件 []. */
  groups?: CadAssemblyGroup[];
}

/** MH · 功能分组(图例用)· 来自 synth_meta.groups. */
export interface CadAssemblyGroup {
  gid: string;
  label_cn: string;
  color_hex: string;
  instance_count: number;
  unique_part_count: number;
}

/** RFC-011 M3 · 装配拆解 → BOM + 唯一零件(首次触发 XCAF 拆解 · 后端缓存). */
export async function fetchCadAssembly(
  documentId: string,
): Promise<CadAssembly> {
  const { data } = await apiClient.get<CadAssembly>(
    `/v1/nc-review/cad-documents/${documentId}/assembly`,
    { _silentError: true, _errorContext: "装配拆解失败" },
  );
  return data;
}

// ─── RFC-011 Temporal · 大装配异步拆解 job(无 45s 墙 · 轮询进度) ───

export interface DecomposeJobStatus {
  job_id: string | null;
  status: "running" | "succeeded" | "failed";
  current_step?: string | null;
  part_count?: number | null;
  error_text?: string | null;
}

/** RFC-011 Temporal · 触发大装配异步拆解 → 返 job(running/succeeded[幂等]/failed[派发失败]). */
export async function createDecomposeJob(
  documentId: string,
): Promise<DecomposeJobStatus> {
  const { data } = await apiClient.post<DecomposeJobStatus>(
    `/v1/nc-review/cad-documents/${documentId}/decompose-jobs`,
    undefined,
    { _silentError: true, _errorContext: "拆解派发失败" },
  );
  return data;
}

/** RFC-011 Temporal · 轮询拆解 job 状态. */
export async function fetchDecomposeJob(
  jobId: string,
): Promise<DecomposeJobStatus> {
  const { data } = await apiClient.get<DecomposeJobStatus>(
    `/v1/nc-review/decompose-jobs/${jobId}`,
    { _silentError: true, _errorContext: "拆解状态查询失败" },
  );
  return data;
}

/** 逐件 3D 预览镶嵌算法版本 · bump 时同步改服务端 cache key(step_to_stl_all_solids 多体修复 = v2)·
 * 作 URL query 串入 → 内容变(算法升级)时换 URL · 绕开浏览器 max-age=3600 缓存的旧 STL。 */
const PART_MESH_VERSION = 2;

/** RFC-011 · 装配逐件 3D 预览 STL · 整体装配过大不可镶嵌时按零件惰性镶嵌 + 缓存(修"模型预览失效"). */
export async function fetchCadPartMesh(
  documentId: string,
  partIndex: number,
  fmt: "stl" | "draco" = "stl", // RFC-014 M2b · draco=压缩网格(整机实例化省传输 · DRACOLoader 解)
): Promise<ArrayBuffer> {
  const { data } = await apiClient.get<ArrayBuffer>(
    `/v1/nc-review/cad-documents/${documentId}/assembly/parts/${partIndex}/mesh`,
    {
      params: { v: PART_MESH_VERSION, format: fmt }, // cache-bust · 见 PART_MESH_VERSION 注释
      responseType: "arraybuffer",
      _silentError: true, // 预览失败由 StepMeshViewer 内联优雅降级 · 不弹 toast
      _errorContext: "逐件网格下载失败",
    },
  );
  return data;
}

/** RFC-011 M3 · 装配中某唯一零件的单视图 SVG 工程图. */
export async function fetchCadPartDrawingSvg(
  documentId: string,
  partIndex: number,
  view: DrawingView = "front",
  sectionOffset?: number,
  dims?: boolean,
): Promise<string> {
  const { data } = await apiClient.get<Blob>(
    `/v1/nc-review/cad-documents/${documentId}/assembly/parts/${partIndex}/drawing`,
    {
      params: drawingParams(view, "svg", sectionOffset, dims),
      responseType: "blob",
      _silentError: true, // 逐件出图失败由 AssemblyPanel 内联提示 · 不弹 toast
      _errorContext: "逐件工程图加载失败",
    },
  );
  return await blobToText(data);
}

/** RFC-011 M3 · 下载装配逐件工程图 · dxf / pdf. */
export async function downloadCadPartDrawing(
  documentId: string,
  partIndex: number,
  view: DrawingView,
  format: "dxf" | "pdf",
  sectionOffset?: number,
): Promise<Blob> {
  const { data } = await apiClient.get<Blob>(
    `/v1/nc-review/cad-documents/${documentId}/assembly/parts/${partIndex}/drawing`,
    {
      params: drawingParams(view, format, sectionOffset),
      responseType: "blob",
      _errorContext: "逐件工程图下载失败",
    },
  );
  return data;
}

/** 下载装配逐件「所见即所得」标注总图 PDF(复用屏幕 compose_sheet_svg · ai 尊重开关).
 *
 * 与 downloadCadPartDrawing(/assembly/parts/{i}/drawing · 老 4 视图自动拆解图)不同 ——
 * 此处走逐件 sheet 端点(format=pdf),导出的就是屏幕预览的同一张标注总图(主视图 + 剖视 +
 * GD&T + 技术参数表 + ai=true 时的 AI 标注)。修复装配逐件「展示 ≠ 下载」。150s 超时同 sheet。
 */
export async function downloadCadPartSheetPdf(
  documentId: string,
  partIndex: number,
  ai = true,
  processKind = "none",
  dimStrategy = "chain",
): Promise<Blob> {
  const params: Record<string, string | number | boolean> = {
    ai,
    v: 3,
    format: "pdf",
  };
  if (processKind !== "none") params.process_kind = processKind;
  if (dimStrategy !== "chain") params.dim_strategy = dimStrategy;
  const { data } = await apiClient.get<Blob>(
    `/v1/nc-review/cad-documents/${documentId}/assembly/parts/${partIndex}/drawing/sheet`,
    {
      params,
      responseType: "blob",
      timeout: 150_000,
      _errorContext: "逐件 PDF 下载失败",
    },
  );
  return data;
}

/** RFC-011 #4 · 装配逐件工程图总图 SVG · 默认自带 AI 标注(懒加载 · 仅查看的零件跑 LLM + 缓存).
 *
 * ``timeoutMs`` 默认 150s:逐件 AI 总图同样 = extract + LLM + 渲染 · 复杂件 >60s · 防 axios 60s 抢断。
 */
export async function fetchCadPartSheet(
  documentId: string,
  partIndex: number,
  ai = true,
  processKind = "none",
  dimStrategy = "chain",
  refresh = false,
  timeoutMs = 150_000,
): Promise<string> {
  const params: Record<string, string | number | boolean> = { ai, v: 3 };
  if (processKind !== "none") params.process_kind = processKind; // RFC-012 M2 · 工艺驱动标注(逐件同单件)
  if (dimStrategy !== "chain") params.dim_strategy = dimStrategy; // RFC-012 M3 · 尺寸策略(逐件同单件)
  if (refresh) {
    params.refresh = true; // 跳服务端缓存 · 强制重渲
    params.t = Date.now(); // 绕浏览器 HTTP 缓存
  }
  const { data } = await apiClient.get<Blob>(
    `/v1/nc-review/cad-documents/${documentId}/assembly/parts/${partIndex}/drawing/sheet`,
    {
      params, // v=3 渲染版本 · refresh 时附 t 绕浏览器缓存(与服务端 cache 配对)
      responseType: "blob",
      timeout: timeoutMs,
      _silentError: true,
      _errorContext: "逐件工程图总图加载失败",
    },
  );
  return await blobToText(data);
}

// 注:AI 荐尺寸/GD&T 方案现【直接画到工程图总图上】(含 AI 标注开关 · 后端 sheet?ai=true)·
// 旧的 fetchAiDimensions + 弹窗表格已下线(避免与开关重复)· 端点 /drawing/ai-dimensions 仍保留。

export interface TargetMeshUploaded {
  receipt?: import("./partIntakes").ArchiveReceipt;
  active_revision_id?: string;
  program_id: string;
  part_id: string;
  target_mesh_key: string;
  filename: string;
}

/** 上传目标零件几何 STL → 设到程序关联 part · 启用闭环过切/欠切检测 (M3). */
export async function uploadTargetMesh(
  programId: string,
  file: File,
  confirmation: {
    expected_active_revision_id: string | null;
    change_reason: string;
    confirm_pairing: boolean;
  },
): Promise<TargetMeshUploaded> {
  const form = new FormData();
  form.append("file", file);
  form.append(
    "expected_active_revision_id",
    confirmation.expected_active_revision_id ?? "",
  );
  form.append("change_reason", confirmation.change_reason);
  form.append("confirm_pairing", String(confirmation.confirm_pairing));
  const response = await apiClient.post<TargetMeshUploaded>(
    `/v1/nc-review/programs/${programId}/target-mesh`,
    form,
    { _errorContext: "目标几何上传失败" },
  );
  return requireVerifiedSourceWrite(response);
}

// ─── RFC-004 · 几何生成(毛坯+成品 STP → AI 生成 NC → 自动接闭环优化)──────────

/** 项目(零件归属 · GET /v1/cnc/projects). */
export interface Project {
  id: string;
  customer_id: string;
  code: string;
  name: string;
  status: string;
  is_active: boolean;
}

export async function listProjects(): Promise<Project[]> {
  const { data } = await apiClient.get<Project[]>("/v1/cnc/projects", {
    params: { is_active: true, limit: 200 },
  });
  return data;
}

/** 零件 · GET/POST /v1/cnc/parts. */
export interface Part {
  id: string;
  project_id: string;
  code: string;
  name: string;
  drawing_no: string | null;
  material: string | null;
  material_std: string | null;
  surface_treat: string | null;
  operation_no: string | null;
  is_active: boolean;
}

export interface PartCreateInput {
  project_id: string;
  code: string;
  name: string;
  drawing_no?: string | null;
  /** 缺失材料必须明确确认，下游保持 needs_input。 */
  material: string | null;
  acknowledge_missing_material?: boolean;
  request_key?: string;
  material_std?: string | null;
  surface_treat?: string | null;
  operation_no?: string | null;
}

/** 常用材料(下拉建议 · 可自定义)· 标题栏 + 重量密度估用。 */
export const COMMON_MATERIALS = [
  "A356-T6",
  "ADC12",
  "6061-T6",
  "7075-T6",
  "45#",
  "Q235",
  "40Cr",
  "304",
  "316L",
  "HT250",
  "QT600-3",
  "TC4",
  "H62",
] as const;

export async function createPart(input: PartCreateInput): Promise<Part> {
  const response = await apiClient.post<PartWriteResponse<Part>>(
    "/v1/cnc/parts",
    input,
    {
      _errorContext: "创建零件失败",
    },
  );
  return requireVerifiedPart(response);
}

/** 零件级几何上传产物(成品 target / 毛坯 stock 共用 · 字段按上传种类返回). */
export interface PartGeometryUploaded {
  receipt?: import("./partIntakes").ArchiveReceipt;
  active_revision_id?: string;
  part_id: string;
  target_mesh_key?: string;
  stock_shape_key?: string;
  source_format: string;
  volume_mm3?: number;
  bbox_mm?: [number, number, number];
  stock_length_mm?: number;
  stock_width_mm?: number;
  stock_height_mm?: number;
}

/** 成品几何(STEP/STL/DXF)→ part.target_mesh_key (+ STEP 存 source_cad_key 供从零合成). */
export async function uploadPartTargetMesh(
  partId: string,
  file: File,
  confirmation: {
    expected_active_revision_id: string | null;
    change_reason: string;
    stock_unit?: string;
    confirm_pairing: boolean;
  },
  depthMm?: number,
): Promise<PartGeometryUploaded> {
  const form = new FormData();
  form.append("file", file);
  form.append(
    "expected_active_revision_id",
    confirmation.expected_active_revision_id ?? "",
  );
  form.append("change_reason", confirmation.change_reason);
  form.append("confirm_pairing", String(confirmation.confirm_pairing));
  if (confirmation.stock_unit)
    form.append("stock_unit", confirmation.stock_unit);
  if (depthMm != null) form.append("depth_mm", String(depthMm));
  const response = await apiClient.post<PartGeometryUploaded>(
    `/v1/nc-review/parts/${partId}/target-mesh`,
    form,
    { _errorContext: "成品几何上传失败" },
  );
  return requireVerifiedSourceWrite(response);
}

/** 毛坯几何(STEP/STL)→ part.stock_shape_key(网格毛坯)+ 自动从 bbox 填 stock_*_mm. */
export async function uploadPartStockMesh(
  partId: string,
  file: File,
  confirmation: {
    expected_active_revision_id: string | null;
    change_reason: string;
    stock_unit?: string;
  },
): Promise<PartGeometryUploaded> {
  const form = new FormData();
  form.append("file", file);
  form.append(
    "expected_active_revision_id",
    confirmation.expected_active_revision_id ?? "",
  );
  form.append("change_reason", confirmation.change_reason);
  if (confirmation.stock_unit)
    form.append("stock_unit", confirmation.stock_unit);
  const response = await apiClient.post<PartGeometryUploaded>(
    `/v1/nc-review/parts/${partId}/stock-mesh`,
    form,
    { _errorContext: "毛坯几何上传失败" },
  );
  return requireVerifiedSourceWrite(response);
}

/** 启动生成 · then_optimize=true 时收敛后自动接闭环优化(仿真→反馈→优化). */
export interface GenerateRequest {
  max_rounds?: number;
  engine_mode?: OptimizeEngineMode;
  sections?: number | null;
  reference_program_id?: string | null;
  machine_id?: string | null;
  then_optimize?: boolean;
  optimize_max_rounds?: number;
  optimize_engine_mode?: OptimizeEngineMode;
}

export interface GenerateAccepted {
  generate_id: string;
  status: "running";
  max_rounds: number;
  /** None = 无相似参考件 → P3 从零 CAM 合成;非空 = P2 参数化改适配源. */
  reference_program_id: string | null;
  status_url: string;
}

export type GenerateStatus =
  | "running"
  | "converged"
  | "generate_failed"
  | "failed";

/** 生成每轮摘要(明细走 getGenerateRoundReport · 复用 ClosedLoopRoundReport 结构). */
export interface GenerateRoundSummary {
  run_id: string;
  round_no: number;
  status: string;
  passed: boolean | null;
  high_count: number | null;
  cut_mesh_key: string | null;
  generated_program_id: string;
  report_url: string;
  mesh_url: string | null;
}

/**
 * 生成轨迹单步 · AgentTurn 超集 (复用 AgentTrajectoryTimeline 渲染) · round_no 分组.
 * kind: tool=LLM 工具轮(P2 改适配) · narration=LLM 推理叙述 · stage=管线阶段
 * (P3 cam_synthesis / emit_program 等) · simulate=该轮仿真验证.
 */
export interface GenerateTrajectoryStep extends AgentTurn {
  round_no: number;
  kind: "tool" | "stage" | "simulate" | "narration";
}

/** V98.29 P0-1 · sim 实时进度信号(由 simulate_activity mid-sim 写盘 · 前端 4s 轮询
    读最新一行作 progress bar 真值来源,替代 V98.26 traj.length / 12 估算)。 */
export interface PartialProgress {
  kind: string;
  sub_kind: string | null;
  /** 0-100 进度估算。 */
  pct: number;
  /** 结构化上下文 {phase, current_setup, setups_count, seg_idx, err, ...}。 */
  payload: Record<string, unknown> | null;
  created_at: string | null;
}

export interface GenerateState {
  generate_id: string;
  part_id: string;
  status: GenerateStatus;
  converged: boolean;
  current_round: number;
  max_rounds: number;
  reference_program_id: string | null;
  generated_program_id: string | null;
  final_review_status: string | null;
  /** 非空 → 收敛后自动派发的闭环优化 loop · 前端据此切到优化进度(pollClosedLoop). */
  optimize_loop_id: string | null;
  error_text: string | null;
  /** AI 执行轨迹 (running 时增量增长 · 4s 轮询实时看 · c037). */
  trajectory?: GenerateTrajectoryStep[];
  rounds: GenerateRoundSummary[];
  /** V98.29 P0-1 · sim 活动 mid-sim 写盘的真进度 (None=老 run 无 partial_signals 行 → 前端 fallback 估算). */
  partial_progress?: PartialProgress | null;
}

export async function startGenerate(
  partId: string,
  params: GenerateRequest = {},
): Promise<GenerateAccepted> {
  const { data } = await apiClient.post<GenerateAccepted>(
    `/v1/nc-review/parts/${partId}/generate`,
    params,
    { _errorContext: "生成启动失败" },
  );
  return data;
}

/** V98.29 P0-2 · 用户主动取消 generation workflow (撞 stall 主动结束 · 不等 30min 兜底) */
export async function cancelGenerate(
  partId: string,
  generateId: string,
): Promise<{
  cancelled: boolean;
  generate_id: string;
  current_status: string;
  reason?: string;
  note?: string;
}> {
  const { data } = await apiClient.post(
    `/v1/nc-review/parts/${partId}/generate/${generateId}/cancel`,
    undefined,
    { _errorContext: "取消生成失败" },
  );
  return data;
}

export async function getGenerateStatus(
  partId: string,
  generateId: string,
): Promise<GenerateState> {
  const { data } = await apiClient.get<GenerateState>(
    `/v1/nc-review/parts/${partId}/generate/${generateId}`,
  );
  return data;
}

/** 生成某轮完整仿真报告(problems[] · 跟闭环 round report 同结构). */
export async function getGenerateRoundReport(
  partId: string,
  generateId: string,
  roundNo: number,
): Promise<ClosedLoopRoundReport> {
  const { data } = await apiClient.get<ClosedLoopRoundReport>(
    `/v1/nc-review/parts/${partId}/generate/${generateId}/rounds/${roundNo}/report`,
  );
  return data;
}

export interface OptimizeHistoryItem {
  audit_id: string;
  occurred_at: string;
  user_id: string | null;
  filename: string;
  dialect: string;
  changes_count: number;
  est_time_saved_seconds: number;
  aborted: boolean;
  abort_reason: string | null;
  warnings_count: number;
  warnings?: string[];
  optimized_object_key: string;
  params: Record<string, unknown>;
  engine_used?: OptimizeEngineMode | null;
  fallback_reason?: string | null;
  agent_summary?: string | null;
  agent_tokens_in?: number;
  agent_tokens_out?: number;
  agent_trajectory?: AgentTurn[];
}

export interface OptimizeHistoryResponse {
  program_id: string;
  count: number;
  items: OptimizeHistoryItem[];
}

export async function getOptimizeHistory(
  programId: string,
): Promise<OptimizeHistoryResponse> {
  const { data } = await apiClient.get<OptimizeHistoryResponse>(
    `/v1/nc-review/programs/${programId}/optimize/history`,
  );
  return data;
}

export interface StatsResponse {
  total: number;
  by_status: { pending: number; approved: number; rejected: number };
  risks_total: RiskCounts;
}

export async function getStats(): Promise<StatsResponse> {
  const { data } = await apiClient.get<StatsResponse>("/v1/nc-review/stats");
  return data;
}

export interface BatchReviewResult {
  signed_count: number;
  decision: "approve" | "reject";
  program_ids: string[];
}

export interface ProgramSummary {
  program_id: string;
  filename: string;
  uploaded_at: string;
  total_lines: number;
  motion_lines: number;
  cycle_count: number;
  review_status: ReviewStatus;
  risks: RiskCounts;
  risk_score: number;
}

export interface DiffMeta {
  added_lines: number;
  removed_lines: number;
  modified_lines: number;
  added_motions: number;
  removed_motions: number;
  risk_score_delta: number;
  verdict: "improved" | "degraded" | "neutral" | "blocked";
}

export interface DiffResult {
  base: ProgramSummary;
  candidate: ProgramSummary;
  meta: DiffMeta;
  unified_diff: string[];
}

export async function diffPrograms(
  basePid: string,
  candidatePid: string,
): Promise<DiffResult> {
  // 静默:对比页自己处理错误态(缺报告→引导分析 / 其它→重试),不弹全局错误 toast。
  const { data } = await apiClient.post<DiffResult>(
    "/v1/nc-review/programs/diff",
    { base_program_id: basePid, candidate_program_id: candidatePid },
    { _silentError: true },
  );
  return data;
}

export interface SimilarProgram {
  program_id: string;
  filename: string;
  uploaded_at: string;
  review_status: ReviewStatus;
  similarity: number;
  risk_a: number;
  risk_b: number;
}

export interface SimilarResponse {
  program_id: string;
  top_k: number;
  results: SimilarProgram[];
}

export async function getSimilarPrograms(
  programId: string,
  topK = 5,
): Promise<SimilarResponse> {
  const { data } = await apiClient.get<SimilarResponse>(
    `/v1/nc-review/programs/${programId}/similar`,
    { params: { top_k: topK } },
  );
  return data;
}

export async function batchReviewPrograms(
  programIds: string[],
  decision: "approve" | "reject",
  remarks: string,
): Promise<BatchReviewResult> {
  const { data } = await apiClient.post<BatchReviewResult>(
    "/v1/nc-review/programs/batch-review",
    { program_ids: programIds, decision, remarks },
    { _errorContext: decision === "approve" ? "批量签字失败" : "批量驳回失败" },
  );
  return data;
}

// ─────────── 报告下载 (Excel / PDF) ───────────

export type ReportFormat =
  | "html"
  | "html_audit"
  | "pdf"
  | "pdf_audit"
  | "xlsx"
  | "xlsx_audit";

export async function downloadProgramReport(
  programId: string,
  format: ReportFormat,
): Promise<Blob> {
  const { data } = await apiClient.get<Blob>(
    `/v1/nc-review/programs/${programId}/report`,
    { params: { format }, responseType: "blob", _errorContext: "报告下载失败" },
  );
  return data;
}

// ─────────── Tool Sheet PDF ───────────

export async function downloadToolSheet(
  programId: string,
  opts: { preview?: boolean; format?: "pdf" | "html" } = {},
): Promise<Blob> {
  const { data } = await apiClient.get<Blob>(
    `/v1/nc-review/programs/${programId}/tool-sheet`,
    {
      params: { format: opts.format ?? "pdf", preview: opts.preview ?? false },
      responseType: "blob",
      _errorContext: "Tool Sheet 下载失败",
      // 让 axios 跟 302 重定向 (默认 axios 不跟 · 但浏览器 fetch 跟 · 这里我们用 preview=false 通常返 302 → 浏览器自动跟去 MinIO)
      maxRedirects: 5,
    },
  );
  return data;
}

export async function regenerateToolSheet(
  programId: string,
): Promise<{ task_id: string; status: string }> {
  const { data } = await apiClient.post<{ task_id: string; status: string }>(
    `/v1/nc-review/programs/${programId}/tool-sheet/regenerate`,
  );
  return data;
}

// ─────────── 租户规则 CRUD ───────────

export type RuleSource =
  | "builtin"
  | "tenant_custom"
  | "tenant_override"
  | "tenant_disabled"
  | "tenant_custom_disabled";

export interface RuleListItem {
  rule_id: string;
  name: string;
  severity: Severity;
  enabled: boolean;
  source: RuleSource;
  confidence: number;
  tags: string[];
  implemented: boolean;
  unimplemented_reason: string | null;
}

export interface RuleDetailResponse {
  rule_id: string;
  source: RuleSource;
  yaml_content: string;
  builtin_yaml_content: string | null;
  enabled: boolean;
  implemented: boolean;
  unimplemented_reason: string | null;
  comment: string | null;
}

export async function getRule(ruleId: string): Promise<RuleDetailResponse> {
  const { data } = await apiClient.get<RuleDetailResponse>(
    `/v1/nc-review/rules/${encodeURIComponent(ruleId)}`,
    { _silentError: true },
  );
  return data;
}

export interface RuleListResponse {
  total: number;
  items: RuleListItem[];
}

export async function listRules(params?: {
  severity?: Severity;
  enabled_only?: boolean;
}): Promise<RuleListResponse> {
  const { data } = await apiClient.get<RuleListResponse>(
    "/v1/nc-review/rules",
    {
      params,
    },
  );
  return data;
}

export interface CreateRulePayload {
  rule_id: string;
  yaml_content?: string;
  is_custom: boolean;
  is_disabled?: boolean;
  comment?: string;
}

export interface UpdateRulePayload {
  yaml_content?: string;
  is_disabled?: boolean;
  comment?: string;
}

export interface CreateRuleResult {
  id: string;
  rule_id: string;
  is_custom: boolean;
  is_disabled: boolean;
  approved_at: string | null;
}

export async function createRule(
  payload: CreateRulePayload,
): Promise<CreateRuleResult> {
  const { data } = await apiClient.post<CreateRuleResult>(
    "/v1/nc-review/rules",
    payload,
    { _errorContext: "规则创建失败" },
  );
  return data;
}

export async function updateRule(
  ruleId: string,
  payload: UpdateRulePayload,
): Promise<{ id: string; rule_id: string; is_disabled: boolean }> {
  const { data } = await apiClient.put(
    `/v1/nc-review/rules/${ruleId}`,
    payload,
    { _errorContext: "规则更新失败" },
  );
  return data as { id: string; rule_id: string; is_disabled: boolean };
}

export async function deleteRule(ruleId: string): Promise<void> {
  await apiClient.delete(`/v1/nc-review/rules/${ruleId}`, {
    _errorContext: "规则删除失败",
  });
}

export async function approveRule(ruleId: string): Promise<{
  rule_id: string;
  approved_by: string;
  approved_at: string;
}> {
  const { data } = await apiClient.post(
    `/v1/nc-review/rules/${ruleId}/approve`,
    undefined,
    { _errorContext: "规则审批失败" },
  );
  return data as { rule_id: string; approved_by: string; approved_at: string };
}

export interface RegressionItem {
  program_id: string;
  filename: string;
  triggered: boolean;
  line_numbers: number[];
  severity_change: "added" | "removed" | "unchanged" | "neutral";
  evaluated?: boolean;
  evaluation_errors?: RuleEvaluationError[];
}

export interface RegressionResult {
  rule_id: string;
  total: number;
  triggered_count: number;
  trigger_rate: number;
  items: RegressionItem[];
  draft_disabled: boolean;
  missing_program_count: number;
}

export async function runRegression(
  yamlContent: string,
  programIds: string[],
): Promise<RegressionResult> {
  const { data } = await apiClient.post<RegressionResult>(
    "/v1/nc-review/rules/regression",
    { yaml_content: yamlContent, program_ids: programIds },
    { _errorContext: "回归测试失败" },
  );
  return data;
}

// ─────────── 厂区驾驶舱 (S09) ───────────

export interface DashboardMonthlyPoint {
  day: string;
  uploads: number;
  risk_a: number;
  risk_b: number;
}

export interface DashboardRiskBreakdown {
  A: number;
  B: number;
  C: number;
  i: number;
}

export interface DashboardTopUserItem {
  user_id: string;
  /** 真实姓名(users 表)· 系统账号→「系统」· 查不到→ null(前端兜底显 user_id 前 8 位)。 */
  name: string | null;
  uploads: number;
  risk_a: number;
}

export interface DashboardTopRefItem {
  part_id?: string;
  machine_id?: string;
  program_count: number;
}

export interface DashboardRoiEstimate {
  rejected_program_count: number;
  intercepted_a: number;
  intercepted_b: number;
  intercepted_value_rmb: number;
  loss_per_a_rmb: number;
  loss_per_b_rmb: number;
  method: string;
  // V72e (2026-05-27) · ROI 公式扩 approved
  approved_program_count?: number;
  rejected_intercepted_value_rmb?: number;
  approved_intercepted_value_rmb?: number;
  rejected_a?: number;
  rejected_b?: number;
  approved_a?: number;
  approved_b?: number;
}

export interface DashboardOptimizationStats {
  optimization_runs: number;
  distinct_programs: number;
  total_changes: number;
  total_time_saved_seconds: number;
  total_time_saved_hours: number;
}

export interface DashboardData {
  tenant_id: string;
  generated_at: string;
  program_assets?: {
    window_days: number;
    total_programs: number;
    uploads_window: number;
    risk_a_total: number;
    risk_a_window: number;
  };
  /** 工厂时区(租户设置 factory_timezone · 默认北京时间)· 趋势按它切天;老后端没有 */
  factory_timezone?: string;
  monthly_trend: DashboardMonthlyPoint[];
  risk_breakdown: DashboardRiskBreakdown;
  top_programmers: DashboardTopUserItem[];
  top_parts: DashboardTopRefItem[];
  top_machines: DashboardTopRefItem[];
  roi_estimate: DashboardRoiEstimate;
  optimization?: DashboardOptimizationStats;
}

export async function getDashboard(): Promise<DashboardData> {
  const { data } = await apiClient.get<DashboardData>(
    "/v1/nc-review/dashboard",
  );
  return data;
}

// ─────────── 控制器实时遥测 (v1.5 Round 3) ───────────

export interface TelemetryMetric {
  metric: string;
  timestamp: string;
  value_num: number | null;
  value_str: string | null;
}

export interface TelemetryCurrentResponse {
  machine_id: string;
  metrics: TelemetryMetric[];
  fetched_at: string;
}

export interface TelemetryHistoryPoint {
  timestamp: string;
  value_num: number | null;
  value_str: string | null;
}

export interface TelemetryHistoryResponse {
  machine_id: string;
  metric: string;
  since_minutes: number;
  count: number;
  points: TelemetryHistoryPoint[];
}

export async function getMachineTelemetryCurrent(
  machineId: string,
  metrics?: string[],
): Promise<TelemetryCurrentResponse> {
  const { data } = await apiClient.get<TelemetryCurrentResponse>(
    `/v1/nc-review/machines/${machineId}/telemetry/current`,
    { params: metrics ? { metrics } : undefined },
  );
  return data;
}

export async function getMachineTelemetryHistory(
  machineId: string,
  metric: string,
  sinceMinutes: number = 60,
  limit: number = 1000,
): Promise<TelemetryHistoryResponse> {
  const { data } = await apiClient.get<TelemetryHistoryResponse>(
    `/v1/nc-review/machines/${machineId}/telemetry/history`,
    { params: { metric, since_minutes: sinceMinutes, limit } },
  );
  return data;
}

// ─────────── 审计追溯 + Webhook 管理 (M1) ───────────

export type AuditResult = "success" | "failure";

export interface AuditEventItem {
  id: string;
  occurred_at: string;
  user_id: string | null;
  user_ip: string | null;
  action: string;
  object_type: string;
  object_id: string;
  result: AuditResult;
  error_code: string | null;
  metadata: Record<string, unknown>;
  signature_hash: string | null;
  trace_id: string;
}

export interface AuditEventListResponse {
  total: number;
  limit: number;
  offset: number;
  items: AuditEventItem[];
}

export async function listAuditEvents(params?: {
  action?: string;
  object_type?: string;
  result?: AuditResult;
  limit?: number;
  offset?: number;
}): Promise<AuditEventListResponse> {
  const { data } = await apiClient.get<AuditEventListResponse>(
    "/v1/nc-review/audit-events",
    { params },
  );
  return data;
}

export interface WebhookListItem {
  id: string;
  name: string;
  target_url: string;
  event_types: string[];
  is_active: boolean;
  max_retries: number;
  timeout_seconds: number;
  last_delivery_at: string | null;
  last_delivery_status: string | null;
  delivery_failure_count: number;
}

export interface WebhookListResponse {
  total: number;
  known_event_types: string[];
  items: WebhookListItem[];
}

export interface WebhookCreatePayload {
  name: string;
  target_url: string;
  secret: string;
  event_types: string[];
  is_active?: boolean;
  max_retries?: number;
  timeout_seconds?: number;
}

export interface WebhookUpdatePayload {
  name?: string;
  target_url?: string;
  secret?: string;
  event_types?: string[];
  is_active?: boolean;
  max_retries?: number;
  timeout_seconds?: number;
}

export async function listWebhooks(): Promise<WebhookListResponse> {
  const { data } = await apiClient.get<WebhookListResponse>(
    "/v1/nc-review/webhooks",
  );
  return data;
}

export async function createWebhook(
  payload: WebhookCreatePayload,
): Promise<WebhookListItem> {
  const { data } = await apiClient.post<WebhookListItem>(
    "/v1/nc-review/webhooks",
    payload,
    { _errorContext: "Webhook 创建失败" },
  );
  return data;
}

export async function updateWebhook(
  webhookId: string,
  payload: WebhookUpdatePayload,
): Promise<{ id: string; is_active: boolean; secret_rotated: boolean }> {
  const { data } = await apiClient.put<{
    id: string;
    is_active: boolean;
    secret_rotated: boolean;
  }>(`/v1/nc-review/webhooks/${webhookId}`, payload, {
    _errorContext: "Webhook 更新失败",
  });
  return data;
}

export async function deleteWebhook(webhookId: string): Promise<void> {
  await apiClient.delete(`/v1/nc-review/webhooks/${webhookId}`, {
    _errorContext: "Webhook 删除失败",
  });
}

// ── RFC-015 · 生成模型 (文本/文档 → 3D) ──

export interface GenerateModelStarted {
  generated_model_id: string;
  status: string;
  status_url: string;
}

export interface GeneratedModelTrajectoryStep {
  seq?: number;
  stage?: string;
  ok?: boolean;
  detail?: string;
}

export interface GeneratedModelStatus {
  generated_model_id: string;
  status: string; // pending|extracting|matching|synthesizing|registering|succeeded|failed
  fidelity: string | null; // manufacturable | concept | null
  cad_document_id: string | null;
  trajectory: GeneratedModelTrajectoryStep[];
  error_text: string | null;
  pps: Record<string, unknown> | null;
  assignments: Record<string, unknown>[] | null;
  family_hint: string | null;
  created_at: string | null;
}

export async function createGeneratedModel(payload: {
  input_text?: string;
  family_hint?: string;
  session_id?: string;
  attachment_ids?: { attachment_id: string; filename: string; mime?: string }[];
  /** RFC-016 · 几何引擎:template(模板)| codegen(逐件可拆)| codegen_single(整机单发·整体连贯). */
  engine?: "template" | "codegen" | "codegen_single";
}): Promise<GenerateModelStarted> {
  const { data } = await apiClient.post<GenerateModelStarted>(
    `/v1/nc-review/generated-models`,
    payload,
    { _errorContext: "模型生成启动失败" },
  );
  return data;
}

export async function getGeneratedModel(
  id: string,
): Promise<GeneratedModelStatus> {
  const { data } = await apiClient.get<GeneratedModelStatus>(
    `/v1/nc-review/generated-models/${id}`,
    { _silentError: true, _errorContext: "生成状态查询失败" },
  );
  return data;
}

/** 文档忠实 2D→3D 转换状态(GET /v1/nc-review/doc-conversions/{id})· DocConversionCard 轮询用。 */
export interface DocConversionStatus {
  doc_conversion_id: string;
  status: string; // ingesting|indexing|facts_extracting|synthesizing|succeeded|failed|unsupported_part
  fidelity: string | null;
  generated_model_id: string | null;
  cad_document_id: string | null;
  page_count: number | null;
  vector_parsed: boolean;
  product: Record<string, unknown> | null;
  envelope: Record<string, unknown> | null;
  segment_count: number;
  conflicts: Record<string, unknown>[];
  resolution_rules: string[];
  has_audit: boolean;
  trajectory: Record<string, unknown>[];
  error_text: string | null;
  created_at: string | null;
}

export async function getDocConversion(
  id: string,
): Promise<DocConversionStatus> {
  const { data } = await apiClient.get<DocConversionStatus>(
    `/v1/nc-review/doc-conversions/${id}`,
    { _silentError: true, _errorContext: "转换状态查询失败" },
  );
  return data;
}

export async function downloadGeneratedStep(documentId: string): Promise<Blob> {
  const { data } = await apiClient.get<Blob>(
    `/v1/nc-review/cad-documents/${documentId}/step`,
    { responseType: "blob", _errorContext: "STEP 下载失败" },
  );
  return data;
}

export async function downloadGeneratedBom(
  documentId: string,
  format: "csv" | "xlsx" | "json" = "csv",
): Promise<Blob> {
  const { data } = await apiClient.get<Blob>(
    `/v1/nc-review/cad-documents/${documentId}/bom`,
    { params: { format }, responseType: "blob", _errorContext: "BOM 下载失败" },
  );
  return data;
}

export async function downloadGeneratedPmi(documentId: string): Promise<Blob> {
  const { data } = await apiClient.get(
    `/v1/nc-review/cad-documents/${documentId}/pmi`,
    { _errorContext: "PMI 获取失败" },
  );
  return new Blob([JSON.stringify(data, null, 2)], {
    type: "application/json",
  });
}
